import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";

import { DiscoveryCard } from "@/components/karma/DiscoveryCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { getAge } from "@/lib/karma/age";
import { computeArchetype } from "@/lib/karma/archetypes";
import { getProfileInsights } from "@/lib/karma/insights";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { computeMatchOverview, personalizeMatchOverview } from "@/lib/karma/match";
import { getShopBundleForCode } from "@/lib/karma/shop";
import { getDestinyTheme } from "@/lib/karma/theme";
import type { KarmaBirthDate, KarmaUser } from "@/lib/karma/types";
import {
  getSupabaseClient,
  getSupabaseConfig,
  getSupabaseStorageKeyFromDirectUrl,
} from "@/lib/supabase/client";
import {
  ensurePrivateMatchForUser,
  ensureStore,
  getMatchById,
  getMe,
  getUserById,
  listMessages,
  appendMessageToMatch,
  deleteMessageById,
  setMe,
} from "@/lib/karma/storage";
import {
  blockUserAndDeleteConversation,
  deleteDirectConversationWithUser,
  deleteDirectMessageById,
  listDirectMessageConversationSummaries,
  listDirectMessagesBetweenUsers,
  sendDirectMessageToUser,
  updateDirectMessageById,
} from "@/lib/supabase/social";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  Heart,
  Lock,
  MessageCircle,
  MoreVertical,
  Search,
  Send,
  SlidersHorizontal,
  Smile,
  Sparkles,
  X,
  Zap,
} from "lucide-react";
import { MEMBERSHIP_FEATURES, MEMBERSHIP_PLANS } from "../../shared/api";

type KesfetTab = "kesfet" | "begeniler" | "sohbet" | "uyum" | "profil";
type LikesSortBy = "newest" | "harmony" | "name";
type DiscoveryGenderFilter = "herkes" | "kadin" | "erkek" | "non_binary";
type BegenilerTab = "liked" | "matches";

const CHAT_PRIVILEGED_EMAILS = new Set(["ragipfehmi@gmail.com"]);
const SOHBET_UNREAD_KEY = "karma_sohbet_unread_v1";
const DISCOVERY_USAGE_STORAGE_KEY = "karma_discovery_usage_v1";
const LIKES_FILTERS_STORAGE_KEY = "karma_likes_filters_v1";
const DISCOVERY_DAILY_LIMIT_FREE = 100;
const CHAT_ACTIVE_LIMIT_FREE = 3;
const DIRECT_MESSAGE_DAILY_LIMIT_WITHOUT_MUTUAL = 5;
const FREE_MEMBERSHIP_FEATURES: Record<string, boolean> = Object.fromEntries(
  MEMBERSHIP_FEATURES.map((feature) => [feature.key, Boolean(feature.includedInFree)]),
);

const SOHBET_EMOJI_SET = ["😀", "😍", "🥰", "😘", "😎", "🔥", "✨", "🌙", "💜", "🫶", "🎉", "🚀"];

function getTodayDateKey() {
  return new Date().toISOString().slice(0, 10);
}

function readDiscoveryUsageMap() {
  try {
    const raw = globalThis.localStorage?.getItem(DISCOVERY_USAGE_STORAGE_KEY) ?? "{}";
    const parsed = JSON.parse(raw) as Record<string, number>;
    return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, Math.max(0, Number(v) || 0)]));
  } catch {
    return {} as Record<string, number>;
  }
}

function writeDiscoveryUsageMap(next: Record<string, number>) {
  try {
    globalThis.localStorage?.setItem(DISCOVERY_USAGE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore storage write failures
  }
}

function readLikesFilterPrefs() {
  try {
    const raw = globalThis.localStorage?.getItem(LIKES_FILTERS_STORAGE_KEY) ?? "";
    if (!raw) {
      return { minHarmony: 0, sortBy: "newest" as LikesSortBy, onlyWithPhoto: false };
    }

    const parsed = JSON.parse(raw) as {
      minHarmony?: unknown;
      sortBy?: unknown;
      onlyWithPhoto?: unknown;
    };

    const minHarmony = Math.max(0, Math.min(100, Number(parsed?.minHarmony) || 0));
    const sortByRaw = String(parsed?.sortBy ?? "newest");
    const sortBy = sortByRaw === "harmony" || sortByRaw === "name" ? (sortByRaw as LikesSortBy) : ("newest" as LikesSortBy);

    return {
      minHarmony,
      sortBy,
      onlyWithPhoto: Boolean(parsed?.onlyWithPhoto),
    };
  } catch {
    return { minHarmony: 0, sortBy: "newest" as LikesSortBy, onlyWithPhoto: false };
  }
}

function writeLikesFilterPrefs(next: { minHarmony: number; sortBy: LikesSortBy; onlyWithPhoto: boolean }) {
  try {
    globalThis.localStorage?.setItem(LIKES_FILTERS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore storage failures
  }
}

function getDailyDiscoveryUsage(userId: string) {
  const key = `${userId}:${getTodayDateKey()}`;
  const map = readDiscoveryUsageMap();
  return Math.max(0, Number(map[key] ?? 0));
}

function tryConsumeDailyDiscovery(userId: string, limit: number) {
  const key = `${userId}:${getTodayDateKey()}`;
  const map = readDiscoveryUsageMap();
  const current = Math.max(0, Number(map[key] ?? 0));
  if (current >= limit) {
    return { allowed: false as const, used: current, remaining: 0 };
  }

  const nextUsed = current + 1;
  map[key] = nextUsed;
  writeDiscoveryUsageMap(map);
  return { allowed: true as const, used: nextUsed, remaining: Math.max(0, limit - nextUsed) };
}

function getConversationKey(aUserId: string, bUserId: string) {
  return [aUserId, bUserId].sort().join("__");
}

function getConversationKeysForPeer(args: {
  peerId: string;
  realtimeMeId: string;
  localMeId: string;
  matches: { aUserId: string; bUserId: string }[];
}) {
  const keys = new Set<string>();
  const peerId = String(args.peerId ?? "").trim();
  const realtimeMeId = String(args.realtimeMeId ?? "").trim();
  const localMeId = String(args.localMeId ?? "").trim();

  if (!peerId) return [] as string[];

  if (realtimeMeId) keys.add(getConversationKey(realtimeMeId, peerId));
  if (localMeId) keys.add(getConversationKey(localMeId, peerId));

  args.matches.forEach((match) => {
    const includesPeer = match.aUserId === peerId || match.bUserId === peerId;
    if (!includesPeer) return;

    const includesRealtimeMe = Boolean(realtimeMeId) && (match.aUserId === realtimeMeId || match.bUserId === realtimeMeId);
    const includesLocalMe = Boolean(localMeId) && (match.aUserId === localMeId || match.bUserId === localMeId);
    if (!includesRealtimeMe && !includesLocalMe) return;

    keys.add(getConversationKey(match.aUserId, match.bUserId));
  });

  return Array.from(keys).filter(Boolean);
}

function getSohbetUnreadMap() {
  try {
    const raw = globalThis.localStorage?.getItem(SOHBET_UNREAD_KEY) ?? "{}";
    const parsed = JSON.parse(raw) as Record<string, number>;
    return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, Math.max(0, Number(v) || 0)]));
  } catch {
    return {} as Record<string, number>;
  }
}

function setSohbetUnreadMap(next: Record<string, number>) {
  try {
    globalThis.localStorage?.setItem(SOHBET_UNREAD_KEY, JSON.stringify(next));
  } catch {
    // ignore storage write failures
  }

  globalThis.dispatchEvent(new CustomEvent("karma-sohbet-unread-changed"));
}

function getSohbetUnreadTotal() {
  return Object.values(getSohbetUnreadMap()).reduce((sum, v) => sum + (Number(v) || 0), 0);
}

function formatSohbetTime(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isAbortLikeError(error: unknown) {
  const anyErr = error as any;
  const name = String(anyErr?.name ?? "").toLowerCase();
  const message = String(anyErr?.message ?? "").toLowerCase();
  return (
    name.includes("abort") ||
    message.includes("abort") ||
    message.includes("aborted") ||
    message.includes("operation was aborted") ||
    message.includes("signal is aborted")
  );
}

function isFailedFetchLikeError(error: unknown) {
  const anyErr = error as any;
  const name = String(anyErr?.name ?? "").toLowerCase();
  const message = String(anyErr?.message ?? anyErr ?? "").toLowerCase();

  return (
    (name === "typeerror" && (message.includes("fetch") || message.includes("network") || message.includes("load"))) ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed")
  );
}

function buildFastSohbetOverview(peer: KarmaUser) {
  const seed = String(peer?.id ?? peer?.displayName ?? "x");
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 10_000;
  }
  const harmony = 62 + (hash % 33);
  return {
    categoryLabel: harmony >= 85 ? "Yüksek Uyum" : harmony >= 74 ? "Uyumlu" : "Pozitif",
    notes: [`${peer.displayName} ile sohbete başlayabilirsin.`],
    scores: {
      dailyHarmony: harmony,
      attraction: Math.max(45, harmony - 8),
      longevity: Math.max(40, harmony - 12),
    },
  };
}

type KarmaProfileRow = {
  id: string;
  handle: string;
  display_name: string;
  birth_date: string | null;
  bio: string | null;
  photo_url: string | null;
  match_enabled: boolean;
  gender?: "kadin" | "erkek" | "non_binary" | "belirtmek_istemiyorum" | null;
  preferred_gender?: "kadin" | "erkek" | "non_binary" | "herkes" | null;
  birth_time: string | null;
  birth_place: string | null;
  interests: string[] | null;
  pref_min_age: number | null;
  pref_max_age: number | null;
  pref_only_match_enabled: boolean | null;
  pref_destiny_numbers: number[] | null;
  pref_heart_desire_numbers: number[] | null;
  pref_expression_numbers: number[] | null;
  pref_karmic_debt_numbers: number[] | null;
};

function normalizeGender(value: unknown): "kadin" | "erkek" | "non_binary" | "belirtmek_istemiyorum" | null {
  if (value === "kadin" || value === "erkek" || value === "non_binary" || value === "belirtmek_istemiyorum") return value;
  return null;
}

function normalizePreferredGender(value: unknown): "kadin" | "erkek" | "non_binary" | "herkes" {
  if (value === "kadin" || value === "erkek" || value === "non_binary" || value === "herkes") return value;
  return "herkes";
}

function normalizeHandle(value: string) {
  return value
    .toLocaleLowerCase("tr-TR")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z_]+/g, "")
    .slice(0, 24);
}

const GENERIC_DISPLAY_NAMES = new Set(["", "kullanıcı", "kullanici", "user", "biri", "üye", "uye"]);

function shouldUseHandleAsDisplayName(value: string) {
  const normalized = String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, " ")
    .trim();
  if (GENERIC_DISPLAY_NAMES.has(normalized)) return true;
  return normalized === "bir fincan sevgi" || normalized.startsWith("bir fincan sevgi ");
}

function formatDisplayNameFromHandle(value: string) {
  return String(value ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toLocaleUpperCase("tr-TR"));
}

function resolveProfileDisplayName(displayName: string, handle: string) {
  const cleanDisplayName = String(displayName ?? "").trim();
  if (!shouldUseHandleAsDisplayName(cleanDisplayName)) return cleanDisplayName;
  return formatDisplayNameFromHandle(handle) || cleanDisplayName || "Kullanıcı";
}

function getDisplayFirstName(displayName: string) {
  const parts = String(displayName ?? "").trim().split(/\s+/).filter(Boolean);
  return parts[0] || "Kullanıcı";
}

function parseBirthDateFromRow(value: string | null): KarmaBirthDate | null {
  if (!value) return null;
  const [y, m, d] = value.split("-").map((v) => Number(v));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return { year: y, month: m, day: d };
}

function toBirthDateIso(value: KarmaBirthDate | null) {
  if (!value) return null;
  return `${String(value.year).padStart(4, "0")}-${String(value.month).padStart(2, "0")}-${String(value.day).padStart(2, "0")}`;
}

function parseBirthDateFromMetadata(metadata: Record<string, unknown> | null | undefined): KarmaBirthDate | null {
  const meta = (metadata ?? {}) as Record<string, unknown>;

  const dayRaw = Number(meta.birth_day ?? meta.birthDay ?? 0);
  const monthRaw = Number(meta.birth_month ?? meta.birthMonth ?? 0);
  const yearRaw = Number(meta.birth_year ?? meta.birthYear ?? 0);

  if (Number.isFinite(dayRaw) && Number.isFinite(monthRaw) && Number.isFinite(yearRaw) && dayRaw > 0 && monthRaw > 0 && yearRaw > 0) {
    return parseBirthDate({
      day: Math.trunc(dayRaw),
      month: Math.trunc(monthRaw),
      year: Math.trunc(yearRaw),
    });
  }

  const rawIso = meta.birth_date ?? meta.birthDate ?? meta.dob;
  const textIso = typeof rawIso === "string" ? rawIso.trim() : "";
  if (!textIso) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(textIso)) {
    return parseBirthDateFromRow(textIso);
  }

  const parts = textIso.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (!parts) return null;

  return parseBirthDate({
    day: Number(parts[1]),
    month: Number(parts[2]),
    year: Number(parts[3]),
  });
}

function getPrimaryPhotoUrl(user: KarmaUser) {
  return user.photos[0]?.url || "/placeholder.svg";
}

function normalizePublicInsight(text: string) {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return "";

  return normalized
    .replace(/^([A-ZÇĞİÖŞÜ][a-zçğıöşü]+(?:\s+[A-ZÇĞİÖŞÜ][a-zçğıöşü]+){0,2})\s*,\s*/u, "")
    .replace(/([.!?]\s+)([A-ZÇĞİÖŞÜ][a-zçğıöşü]+(?:\s+[A-ZÇĞİÖŞÜ][a-zçğıöşü]+){0,2})\s*,\s*/gu, "$1")
    .replace(/\b[Kk]ader sayın\b/g, "Kader sayısı")
    .replace(/\b[Kk]alp arzun\b/g, "Kalp arzusu")
    .replace(/\b[İIıi]fade numaran\b/g, "İfade numarası")
    .replace(/\b[Kk]işilik numaran\b/g, "Kişilik numarası")
    .replace(/\b[Dd]oğum günü rakamın\b/g, "Doğum günü numarası")
    .replace(/\b[Ee]nerjin\b/g, "enerjisi")
    .replace(/\b[Yy]olun\b/g, "yolu")
    .replace(/\b[Tt]itreşimin\b/g, "titreşimi")
    .replace(/\b[İi]htiyacın\b/g, "ihtiyacı")
    .replace(/\b[Yy]eteneklerin\b/g, "yetenekleri")
    .replace(/\b[Yy]ansıttığın\b/g, "yansıttığı")
    .replace(/\b[Kk]urduğun\b/g, "kurduğu")
    .replace(/\b[Tt]aşıdığın\b/g, "taşıdığı")
    .replace(/\b[Dd]önüştürdüğün\b/g, "dönüştürdüğü")
    .replace(/\b[Hh]issettirdiğin\b/g, "hissettirdiği")
    .replace(/\b[Ss]ahip olduğun\b/g, "sahip olduğu")
    .replace(/\b[Aa]ldığın\b/g, "aldığı")
    .replace(/\b[Yy]aptığın\b/g, "yaptığı")
    .replace(/\b[Ss]eçtiğin\b/g, "seçtiği")
    .replace(/\b[Oo]lduğun\b/g, "olduğu")
    .replace(/([a-zçğıöşü]+)dığın\b/gu, "$1dığı")
    .replace(/([a-zçğıöşü]+)diğin\b/gu, "$1diği")
    .replace(/([a-zçğıöşü]+)duğun\b/gu, "$1duğu")
    .replace(/([a-zçğıöşü]+)düğün\b/gu, "$1düğü")
    .replace(/([a-zçğıöşü]+)tığın\b/gu, "$1tığı")
    .replace(/([a-zçğıöşü]+)tiğin\b/gu, "$1tiği")
    .replace(/([a-zçğıöşü]+)tuğun\b/gu, "$1tuğu")
    .replace(/([a-zçğıöşü]+)tüğün\b/gu, "$1tüğü")
    .replace(/\b[Ss]enin\b/g, "onun")
    .replace(/\b[Ss]ana\b/g, "ona")
    .replace(/\b[Ss]eni\b/g, "onu")
    .replace(/\b[Ss]en\b/g, "o")
    .replace(/\b[Kk]endini\b/g, "kendisini")
    .replace(/\b[Kk]endin\b/g, "kendisi")
    .replace(/\b[Gg]eldin\b/g, "gelir")
    .replace(/\b[Gg]eldiğin\b/g, "geldiği")
    .replace(/\b[Yy]aşarsın\b/g, "yaşar")
    .replace(/\b[Ee]dersin\b/g, "eder")
    .replace(/\b[Oo]\s+bu\s+hayata\b/g, "Bu hayata")
    .replace(/\bduhunun\b/gi, "ruhunun")
    .replace(/dönüştürdüğüü/gi, "dönüştürdüğü")
    .replace(/yansıttığın titreşim/gi, "yansıttığı titreşim")
    .replace(/dığıı/gi, "dığı")
    .replace(/diğii/gi, "diği")
    .replace(/duğuu/gi, "duğu")
    .replace(/düğüü/gi, "düğü")
    .replace(/tığıı/gi, "tığı")
    .replace(/tiğii/gi, "tiği");
}

function hasMeaningfulText(value: unknown): value is string {
  if (typeof value !== "string") return false;
  return value.replace(/[\s\u00a0\u200b\u200c\u200d]+/g, "").length > 0;
}

const RELATIONSHIP_PREVIEW_THEMES: Record<number, string> = {
  1: "birlikte hızlı başlatma ve karar alma",
  2: "duyguyu konuşarak denge kurma",
  3: "iletişim ve neşeyle yakınlaşma",
  4: "güven ve düzenli ilişki zemini",
  5: "değişimle canlı kalma",
  6: "aidiyet ve sorumluluk paylaşımı",
  7: "derin sohbet ve anlam arayışı",
  8: "hedef odaklı ilerleme ve sonuç alma",
  9: "anlam, şefkat ve tamamlama",
};

function buildSohbetPreview(entry: { user: KarmaUser; overview: ReturnType<typeof computeMatchOverview> }) {
  const numerology = computeKarmaNumerology(entry.user.displayName, entry.user.birthDate);
  const harmony = entry.overview.scores.dailyHarmony;
  const relationshipTheme = RELATIONSHIP_PREVIEW_THEMES[entry.overview.relationshipNumber] ?? "ortak bir ritim yakalama";

  const tone =
    harmony >= 85
      ? "İletişim akışı çok güçlü ve birbirinizi hızlı anlıyorsunuz."
      : harmony >= 70
        ? "İletişim dengeli; doğru dille bağ rahatça güçleniyor."
        : harmony >= 55
          ? "Uyum potansiyeli var; net ifade ve sabırlı dinleme ilişkiyi büyütür."
          : "Ritimler farklı olabilir; açık konuşma ve sınırları netleştirme fayda sağlar.";

  return `${tone} Ortak yolunuz ${entry.overview.relationshipNumber}; bu eşleşmede ana tema ${relationshipTheme}.`;
}

function extractPhotoUrlsFromProfileRow(row: KarmaProfileRow): string[] {
  const dynamicRow = row as any;

  const collectFromUnknown = (value: unknown): string[] => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .map((entry) => {
          if (typeof entry === "string") return entry.trim();
          if (entry && typeof entry === "object" && typeof (entry as any).url === "string") {
            return String((entry as any).url).trim();
          }
          return "";
        })
        .filter(Boolean);
    }

    if (typeof value === "string") {
      const text = value.trim();
      if (!text) return [];
      if (text.startsWith("[") && text.endsWith("]")) {
        try {
          return collectFromUnknown(JSON.parse(text));
        } catch {
          return [];
        }
      }
      return [text];
    }

    return [];
  };

  const urls = [
    ...collectFromUnknown(dynamicRow.photos),
    ...collectFromUnknown(dynamicRow.photo_urls),
    ...collectFromUnknown(row.photo_url),
  ];

  return Array.from(new Set(urls)).filter(Boolean).slice(0, 5);
}

function toKarmaUser(row: KarmaProfileRow): KarmaUser | null {
  const bd = parseBirthDateFromRow(row.birth_date) ?? { year: 2000, month: 1, day: 1 };

  const displayName = resolveProfileDisplayName(row.display_name, row.handle);
  const photoUrls = extractPhotoUrlsFromProfileRow(row);

  return {
    id: row.id,
    handle: row.handle,
    displayName,
    birthDate: bd,
    bio: row.bio ?? "",
    photos: photoUrls.map((url, index) => ({
      id: index === 0 ? "main" : `photo_${index + 1}`,
      url,
      addedAt: new Date().toISOString(),
    })),
    interests: Array.isArray(row.interests) ? row.interests.filter((item) => typeof item === "string") : [],
    matchEnabled: !!row.match_enabled,
    likes: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function resolveKesfetTabFromPath(pathname: string): KesfetTab {
  if (pathname.startsWith("/panel/kesfet/begeniler")) return "begeniler";
  if (pathname.startsWith("/panel/kesfet/sohbet")) return "sohbet";
  if (pathname.startsWith("/panel/kesfet/uyum/")) return "uyum";
  if (pathname.startsWith("/panel/kesfet/profil/")) return "profil";
  return "kesfet";
}

function ProfileInsightRow({
  user,
  overview,
  actionLabel,
  actionIcon,
  onAction,
}: {
  user: KarmaUser;
  overview: ReturnType<typeof computeMatchOverview>;
  actionLabel: string;
  actionIcon?: "heart" | "chat";
  onAction: () => void;
}) {
  const numerology = useMemo(
    () => computeKarmaNumerology(user.displayName, user.birthDate),
    [user.displayName, user.birthDate],
  );
  const photo = useMemo(() => getPrimaryPhotoUrl(user), [user]);
  const age = useMemo(() => getAge(user.birthDate), [user.birthDate]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/40">
      <div className="flex items-start gap-3">
        <div className="size-14 overflow-hidden rounded-xl bg-slate-200 dark:bg-slate-800">
          <img src={photo} alt={user.displayName} className="h-full w-full object-cover" loading="lazy" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-extrabold text-slate-900 dark:text-white">
            {user.displayName}, {age}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-white/10">Kader {numerology.destinyNumber}</span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-white/10">Kalp {numerology.heartDesireNumber}</span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 dark:bg-white/10">İfade {numerology.expressionNumber}</span>
            <span className="rounded-full bg-[var(--panel-primary)]/10 px-2.5 py-1 text-[var(--panel-primary)]">
              Uyum {overview.scores.dailyHarmony}/100
            </span>
          </div>
        </div>

        <Button
          type="button"
          className="shrink-0 rounded-full bg-[var(--panel-primary)] px-4 text-xs font-extrabold text-white hover:brightness-110"
          onClick={onAction}
        >
          {actionIcon === "chat" ? <MessageCircle className="mr-2 h-4 w-4" /> : <Heart className="mr-2 h-4 w-4" />}
          {actionLabel}
        </Button>
      </div>

      <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600 dark:bg-white/5 dark:text-slate-300">
        {overview.notes[0]}
      </div>
    </div>
  );
}

function LikesShowcaseCard({
  entry,
  onConnect,
  onOpenReport,
  onOpenProfile,
}: {
  entry: { user: KarmaUser; overview: ReturnType<typeof computeMatchOverview> };
  onConnect: () => void;
  onOpenReport: () => void;
  onOpenProfile: () => void;
}) {
  const numerology = useMemo(
    () => computeKarmaNumerology(entry.user.displayName, entry.user.birthDate),
    [entry.user.displayName, entry.user.birthDate],
  );
  const photo = useMemo(() => getPrimaryPhotoUrl(entry.user), [entry.user]);
  const age = useMemo(() => getAge(entry.user.birthDate), [entry.user.birthDate]);

  return (
    <div className="relative overflow-hidden rounded-[2rem] bg-slate-50/85 p-5 text-center shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12),0_10px_25px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:bg-slate-900/35">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[var(--panel-primary)]/8 via-transparent to-[var(--panel-primary)]/4" />
      <button type="button" onClick={onOpenProfile} className="relative mx-auto mb-4 block size-24 rounded-full p-[3px]">
        <div className="absolute inset-0 rounded-full bg-[var(--panel-primary)]/40 blur-xl" />
        <img src={photo} alt={entry.user.displayName} className="relative size-full rounded-full object-cover" loading="lazy" />
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-[var(--panel-primary)] px-3 py-1 text-[10px] font-extrabold text-white">
          %{entry.overview.scores.dailyHarmony} UYUM
        </div>
      </button>

      <button type="button" onClick={onOpenProfile} className="relative mt-3 text-xl font-extrabold text-[#2e1d35] transition hover:text-[var(--panel-primary)] dark:text-slate-100">
        {entry.user.displayName}, {age}
      </button>
      <div className="relative mt-1 line-clamp-1 text-xs font-medium italic text-[#5d3c67] dark:text-slate-300">
        {entry.user.bio?.trim() || "Numerolojik uyumla derin bir bağ arıyorum."}
      </div>

      <div className="relative mt-4 flex flex-wrap items-center justify-center gap-2">
        <span className="rounded-full border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/10 px-3 py-1 text-[10px] font-semibold text-[var(--panel-primary)]">
          Yol Sayısı {numerology.destinyNumber}
        </span>
        <span className="rounded-full border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/10 px-3 py-1 text-[10px] font-semibold text-[var(--panel-primary)]">
          Ruh Arzusu {numerology.heartDesireNumber}
        </span>
      </div>

      <div className="relative mt-5 space-y-3">
        <button
          type="button"
          onClick={onConnect}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--panel-primary)] py-3 text-sm font-extrabold text-white transition hover:brightness-110"
        >
          <Zap className="h-4 w-4" /> {getDisplayFirstName(entry.user.displayName)} ile Bağ Kur
        </button>
        <button
          type="button"
          onClick={onOpenReport}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#5d3c67] transition hover:text-[var(--panel-primary)] dark:text-slate-300"
        >
          Uyum Raporunu Gör <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function LikesLockedCard({ onUpgrade }: { onUpgrade?: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-[2rem] bg-slate-50/85 p-6 text-center shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12),0_10px_25px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:bg-slate-900/35">
      <div className="absolute inset-0 grid place-items-center bg-slate-50/92 backdrop-blur-sm dark:bg-slate-900/70">
        <div className="mx-auto max-w-[220px]">
          <div className="mx-auto mb-2 grid size-10 place-items-center rounded-full bg-[var(--panel-primary)]/20 text-[var(--panel-primary)]">
            <Lock className="h-5 w-5" />
          </div>
          <div className="text-xs font-bold text-slate-700 dark:text-slate-100">Bu profili ve diğer ruhları görmek için Premium'a geç.</div>
          <button
            type="button"
            onClick={onUpgrade}
            className="mt-4 rounded-full bg-[var(--panel-primary)] px-4 py-2 text-xs font-bold text-white transition hover:brightness-110"
          >
            Planları İncele
          </button>
        </div>
      </div>
      <div className="h-[270px]" />
    </div>
  );
}

function RecommendedProfileCard({
  user,
  harmony,
  onLike,
}: {
  user: KarmaUser;
  harmony: number;
  onLike?: () => void;
}) {
  const numerology = useMemo(
    () => computeKarmaNumerology(user.displayName, user.birthDate),
    [user.displayName, user.birthDate],
  );
  const archetype = useMemo(() => computeArchetype(numerology), [numerology]);
  const theme = useMemo(() => getDestinyTheme(numerology.destinyNumber), [numerology.destinyNumber]);
  const photo = useMemo(() => getPrimaryPhotoUrl(user), [user]);
  const age = useMemo(() => getAge(user.birthDate), [user.birthDate]);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[2rem] border bg-white p-5 shadow-sm",
        "dark:bg-slate-900/30",
        theme.ring,
      )}
    >
      <div className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br opacity-70", theme.gradient)} />

      <button
        type="button"
        onClick={onLike}
        className="absolute right-5 top-5 text-slate-400 transition-colors hover:text-red-500"
        aria-label="Beğen"
      >
        <Heart className="h-4 w-4" />
      </button>

      <div className="relative flex gap-4">
        <div className="size-16 overflow-hidden rounded-2xl bg-slate-200 dark:bg-slate-800">
          <img src={photo} alt={user.displayName} className="h-full w-full object-cover" loading="lazy" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-lg font-extrabold text-slate-900 dark:text-white">{user.displayName}</div>
            <div className="text-sm font-semibold text-slate-500 dark:text-slate-400">{age}</div>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <div className={cn("grid size-6 place-items-center rounded-full text-[10px] font-bold", theme.badge)}>
                {numerology.destinyNumber}
              </div>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Kader</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="grid size-6 place-items-center rounded-full bg-[var(--panel-primary)] text-[10px] font-bold text-white">
                {numerology.heartDesireNumber}
              </div>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Kalp</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="grid size-6 place-items-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                {numerology.expressionNumber}
              </div>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">İfade</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="grid size-6 place-items-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                {archetype.digit}
              </div>
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">Arketip</div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-4 flex w-fit items-center gap-2 rounded-xl bg-white/50 px-3 py-2 text-[10px] font-extrabold text-[var(--panel-primary)] dark:bg-white/5">
        <Sparkles className="h-4 w-4" />
        Eşleşmeye açık
        <span className="ml-1 rounded-full bg-[var(--panel-primary)]/10 px-2 py-0.5 text-[10px] font-black">Uyum {harmony}/100</span>
      </div>

      <div className="relative mt-3 line-clamp-2 text-xs font-medium text-slate-500 dark:text-slate-400">
        {user.bio || "Dönüşüm yolculuğunda; günlük pratikler ve numerolojik farkındalık..."}
      </div>
    </div>
  );
}

async function ensureMyKarmaProfile(userId: string) {
  const supabase = getSupabaseClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const sessionUser = sessionData.session?.user;

  if (!sessionUser || sessionUser.id !== userId) {
    throw new Error("Oturum yok");
  }

  const selectLegacy =
    "id, handle, display_name, birth_date, bio, photo_url, match_enabled, pref_min_age, pref_max_age, pref_only_match_enabled, pref_destiny_numbers, pref_heart_desire_numbers, pref_expression_numbers, pref_karmic_debt_numbers";
  const selectWithGender = `${selectLegacy}, gender, preferred_gender`;

  let data: any = null;
  let error: any = null;
  let hasGenderColumns = true;

  {
    const result = await supabase.from("karma_profiles").select(selectWithGender).eq("id", userId).maybeSingle();
    data = result.data;
    error = result.error;

    const msg = String(error?.message ?? "").toLowerCase();
    const missingGenderColumns = msg.includes("column") && (msg.includes("gender") || msg.includes("preferred_gender"));
    if (error && missingGenderColumns) {
      hasGenderColumns = false;
      const legacy = await supabase.from("karma_profiles").select(selectLegacy).eq("id", userId).maybeSingle();
      data = legacy.data;
      error = legacy.error;
    }
  }

  const meta = (sessionUser.user_metadata ?? {}) as any;

  const first = typeof meta.first_name === "string" ? meta.first_name : "";
  const last = typeof meta.last_name === "string" ? meta.last_name : "";
  const email = typeof sessionUser.email === "string" ? sessionUser.email : "";
  const metadataGender = normalizeGender(meta.gender);
  const metadataPreferredGender = normalizePreferredGender(meta.preferred_gender);
  const displayName = `${first} ${last}`.trim() || (email ? email.split("@")[0] : "Kullanıcı");

  if (!error && data) {
    const currentDisplayName = String((data as any).display_name ?? "").trim();
    const currentBirthDate = String((data as any).birth_date ?? "").trim();
    const normalizedCurrent = currentDisplayName.toLocaleLowerCase("tr-TR");
    const normalizedFallback = displayName.trim().toLocaleLowerCase("tr-TR");
    const needsDisplayNameRepair = shouldUseHandleAsDisplayName(currentDisplayName);

    const metadataBirthDate = parseBirthDateFromMetadata(meta);
    const metadataBirthDateIso = toBirthDateIso(metadataBirthDate);
    const needsBirthDateRepair = !currentBirthDate && Boolean(metadataBirthDateIso);

    if ((needsDisplayNameRepair && normalizedFallback && normalizedCurrent !== normalizedFallback) || needsBirthDateRepair) {
      const patch: Record<string, unknown> = {};
      if (needsDisplayNameRepair && normalizedFallback && normalizedCurrent !== normalizedFallback) {
        patch.display_name = displayName;
      }
      if (needsBirthDateRepair && metadataBirthDateIso) {
        patch.birth_date = metadataBirthDateIso;
      }

      if (hasGenderColumns && !data.gender && metadataGender) {
        patch.gender = metadataGender;
      }

      if (hasGenderColumns && !data.preferred_gender && metadataPreferredGender) {
        patch.preferred_gender = metadataPreferredGender;
      }

      const { data: patched, error: patchError } = await supabase
        .from("karma_profiles")
        .update(patch)
        .eq("id", userId)
        .select(hasGenderColumns ? selectWithGender : selectLegacy)
        .single();

      if (!patchError && patched) {
        return patched as KarmaProfileRow;
      }
    }

    return data as KarmaProfileRow;
  }

  const baseHandle = normalizeHandle(typeof meta.handle === "string" ? meta.handle : displayName || "user");
  const fallback = `user_${userId.slice(0, 8)}`;
  const base = baseHandle || fallback;

  const handleCandidates = [
    base,
    `${base}_${userId.slice(0, 4)}`,
    `${base}_${userId.slice(0, 8)}`,
  ];

  const birthDay = typeof meta.birth_day === "number" ? meta.birth_day : Number(meta.birth_day);
  const birthMonth = typeof meta.birth_month === "number" ? meta.birth_month : Number(meta.birth_month);
  const birthYear = typeof meta.birth_year === "number" ? meta.birth_year : Number(meta.birth_year);

  let birthDate: string | null = null;
  if (Number.isFinite(birthDay) && Number.isFinite(birthMonth) && Number.isFinite(birthYear)) {
    const parsed = parseBirthDate({ day: birthDay, month: birthMonth, year: birthYear });
    const yyyy = String(parsed.year).padStart(4, "0");
    const mm = String(parsed.month).padStart(2, "0");
    const dd = String(parsed.day).padStart(2, "0");
    birthDate = `${yyyy}-${mm}-${dd}`;
  }

  const photoUrl = typeof meta.avatar_url === "string" ? meta.avatar_url.trim() : "";

  let upsertError: any = null;

  for (const handle of handleCandidates) {
    const payload: Record<string, unknown> = {
      id: userId,
      handle,
      display_name: displayName,
      birth_date: birthDate,
      match_enabled: true,
      pref_min_age: 18,
      pref_max_age: 99,
      pref_only_match_enabled: false,
      pref_destiny_numbers: [],
      pref_heart_desire_numbers: [],
      pref_expression_numbers: [],
      pref_karmic_debt_numbers: [],
    };

    if (photoUrl) {
      payload.photo_url = photoUrl;
    }

    if (hasGenderColumns) {
      payload.gender = metadataGender;
      payload.preferred_gender = metadataPreferredGender;
    }

    const res = await supabase.from("karma_profiles").upsert(payload, { onConflict: "id" });

    upsertError = res.error;
    if (!upsertError) break;
  }

  if (upsertError) {
    throw new Error("Profil oluşturulamadı.");
  }

  const { data: created, error: createdError } = await supabase
    .from("karma_profiles")
    .select(hasGenderColumns ? selectWithGender : selectLegacy)
    .eq("id", userId)
    .single();

  if (createdError) throw createdError;
  return created as KarmaProfileRow;
}

async function fetchMyInteractionSets(userId: string) {
  const supabase = getSupabaseClient();

  const [likesRes, passesRes, blocksRes] = await Promise.all([
    supabase.from("karma_likes").select("to_user_id").eq("from_user_id", userId),
    supabase.from("karma_passes").select("to_user_id").eq("from_user_id", userId),
    supabase.from("karma_blocks").select("to_user_id").eq("from_user_id", userId),
  ]);

  if (likesRes.error) throw likesRes.error;
  if (passesRes.error) throw passesRes.error;
  if (blocksRes.error) throw blocksRes.error;

  return {
    liked: new Set((likesRes.data ?? []).map((r: any) => String(r.to_user_id))),
    passed: new Set((passesRes.data ?? []).map((r: any) => String(r.to_user_id))),
    blocked: new Set((blocksRes.data ?? []).map((r: any) => String(r.to_user_id))),
  };
}

export default function UserPanelKesfet() {
  const { user } = useSupabaseSession();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { targetUserId = "" } = useParams();

  const [cursor, setCursor] = useState(0);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [compatibilityLikeOverride, setCompatibilityLikeOverride] = useState<Record<string, boolean>>({});
  const [sohbetQuery, setSohbetQuery] = useState("");
  const [storeVersion, setStoreVersion] = useState(0);
  const [sohbetMessage, setSohbetMessage] = useState("");
  const [sohbetUnreadTotal, setSohbetUnreadTotal] = useState(() => getSohbetUnreadTotal());
  const [dailyDiscoveryUsed, setDailyDiscoveryUsed] = useState(0);
  const [typingMap, setTypingMap] = useState<Record<string, boolean>>({});
  const [activeMessageMenuId, setActiveMessageMenuId] = useState("");
  const [editingMessageId, setEditingMessageId] = useState("");
  const [editingMessageText, setEditingMessageText] = useState("");
  const [hiddenMessageIds, setHiddenMessageIds] = useState<Record<string, true>>({});
  const [showSohbetHeaderMenu, setShowSohbetHeaderMenu] = useState(false);
  const [activeRecentChatMenuPeerId, setActiveRecentChatMenuPeerId] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiBursts, setEmojiBursts] = useState<Array<{ id: string; emoji: string; x: number }>>([]);
  const [showLikesSettings, setShowLikesSettings] = useState(false);
  const [likesMinHarmony, setLikesMinHarmony] = useState(() => readLikesFilterPrefs().minHarmony);
  const [likesSortBy, setLikesSortBy] = useState<LikesSortBy>(() => readLikesFilterPrefs().sortBy);
  const [likesOnlyWithPhoto, setLikesOnlyWithPhoto] = useState(() => readLikesFilterPrefs().onlyWithPhoto);
  const [activeBegenilerTab, setActiveBegenilerTab] = useState<BegenilerTab>("liked");
  const [showAllNewMatches, setShowAllNewMatches] = useState(false);
  const [showDiscoveryFilters, setShowDiscoveryFilters] = useState(false);
  const [discoveryMinAge, setDiscoveryMinAge] = useState(18);
  const [discoveryMaxAge, setDiscoveryMaxAge] = useState(99);
  const [discoveryGender, setDiscoveryGender] = useState<DiscoveryGenderFilter>("herkes");
  const [discoveryDestinyNumber, setDiscoveryDestinyNumber] = useState(0);
  const [discoveryHeartNumber, setDiscoveryHeartNumber] = useState(0);
  const [discoveryExpressionNumber, setDiscoveryExpressionNumber] = useState(0);
  const likesSettingsRef = useRef<HTMLDivElement | null>(null);
  const discoveryFiltersRef = useRef<HTMLDivElement | null>(null);
  const sohbetRealtimeRef = useRef<Record<string, any>>({});
  const sohbetThreadRef = useRef<HTMLDivElement | null>(null);
  const profileEnsureSignatureRef = useRef("");
  const typingResetTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const typingStopBroadcastRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingBroadcastedRef = useRef(false);
  const sohbetRealtimeReadyRef = useRef<Record<string, boolean>>({});
  const shouldAutoScrollSohbetRef = useRef(true);
  const lastSohbetTailRef = useRef("");

  const activeTab = resolveKesfetTabFromPath(location.pathname);

  const pickReadySohbetChannel = useCallback((conversationKeys: string[]) => {
    for (const key of conversationKeys) {
      if (!sohbetRealtimeReadyRef.current[key]) continue;
      const channel = sohbetRealtimeRef.current[key];
      if (channel) return { channel, conversationKey: key };
    }
    return null;
  }, []);

  const waitForReadySohbetChannel = useCallback(
    async (conversationKeys: string[], timeoutMs = 1800) => {
      const readyNow = pickReadySohbetChannel(conversationKeys);
      if (readyNow) return readyNow;

      const startedAt = Date.now();
      while (Date.now() - startedAt < timeoutMs) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 80);
        });

        const ready = pickReadySohbetChannel(conversationKeys);
        if (ready) return ready;
      }

      return null;
    },
    [pickReadySohbetChannel],
  );
  const canChatEveryone = useMemo(() => {
    const email = typeof (user as any)?.email === "string" ? String((user as any).email).trim().toLocaleLowerCase("tr-TR") : "";
    return CHAT_PRIVILEGED_EMAILS.has(email);
  }, [user]);

  const selectedSohbetMatchId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("match") ?? "";
  }, [location.search]);
  const selectedSohbetPeerIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("peer") ?? "";
  }, [location.search]);

  const store = useMemo(() => {
    storeVersion;
    return ensureStore();
  }, [storeVersion]);
  const me = useMemo(() => getMe(store), [store]);
  const realtimeMeId = useMemo(() => {
    const authId = typeof user?.id === "string" ? user.id.trim() : "";
    return authId || me.id;
  }, [me.id, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    try {
      const previousAuthUserId = globalThis.localStorage?.getItem("karma_last_auth_user_id") ?? "";
      const sessionBootUserId = globalThis.sessionStorage?.getItem("karma_store_session_user_id") ?? "";
      const rawStore = globalThis.localStorage?.getItem("karma_store_v2") ?? "";

      let storedMeId = "";
      if (rawStore) {
        try {
          const parsed = JSON.parse(rawStore) as any;
          storedMeId = typeof parsed?.meId === "string" ? parsed.meId : "";
        } catch {
          storedMeId = "";
        }
      }

      const shouldResetForAuthSwitch = Boolean(previousAuthUserId && previousAuthUserId !== user.id);
      const shouldResetForStoreMismatch = Boolean(storedMeId && storedMeId !== user.id);
      const shouldResetForSessionBootstrap = sessionBootUserId !== user.id;

      if (shouldResetForAuthSwitch || shouldResetForStoreMismatch || shouldResetForSessionBootstrap) {
        globalThis.localStorage?.removeItem("karma_store_v2");
        globalThis.localStorage?.removeItem("karma_store_v1");
        globalThis.localStorage?.removeItem(SOHBET_UNREAD_KEY);
        setStoreVersion((v) => v + 1);
      }

      globalThis.localStorage?.setItem("karma_last_auth_user_id", user.id);
      globalThis.sessionStorage?.setItem("karma_store_session_user_id", user.id);
    } catch {
      // ignore storage failures
    }
  }, [user?.id]);
  const selectedSohbetMatch = useMemo(
    () => (selectedSohbetMatchId ? getMatchById(selectedSohbetMatchId, store) : null),
    [selectedSohbetMatchId, store],
  );
  const selectedSohbetOtherUser = useMemo(() => {
    if (selectedSohbetPeerIdFromQuery) {
      const userFromQuery = getUserById(selectedSohbetPeerIdFromQuery, store);
      if (userFromQuery) return userFromQuery;
    }

    if (!selectedSohbetMatch) return null;

    const isARealtime = selectedSohbetMatch.aUserId === realtimeMeId;
    const isBRealtime = selectedSohbetMatch.bUserId === realtimeMeId;
    const isALocal = selectedSohbetMatch.aUserId === me.id;
    const isBLocal = selectedSohbetMatch.bUserId === me.id;

    const otherId = isARealtime || isALocal ? selectedSohbetMatch.bUserId : isBRealtime || isBLocal ? selectedSohbetMatch.aUserId : selectedSohbetMatch.bUserId;

    return getUserById(otherId, store);
  }, [me.id, realtimeMeId, selectedSohbetMatch, selectedSohbetPeerIdFromQuery, store]);
  const selectedSohbetPeerId = selectedSohbetOtherUser?.id ?? "";
  const selectedSohbetConversationKeys = useMemo(() => {
    if (!selectedSohbetPeerId) return [] as string[];
    return getConversationKeysForPeer({
      peerId: selectedSohbetPeerId,
      realtimeMeId,
      localMeId: me.id,
      matches: store.matches,
    });
  }, [me.id, realtimeMeId, selectedSohbetPeerId, store.matches]);

  const selectedSohbetDbThreadQuery = useQuery({
    queryKey: ["karma-direct-thread", user?.id, selectedSohbetPeerId],
    enabled: Boolean(user?.id) && Boolean(selectedSohbetPeerId),
    staleTime: 2_000,
    refetchInterval: activeTab === "sohbet" && Boolean(selectedSohbetPeerId) ? 1500 : false,
    refetchOnWindowFocus: true,
    retry: false,
    queryFn: async () => {
      if (!selectedSohbetPeerId) return [] as Array<{ id: string; fromUserId: string; toUserId: string; text: string; createdAt: string }>;

      try {
        return await listDirectMessagesBetweenUsers({ peerUserId: selectedSohbetPeerId, limit: 500 });
      } catch {
        return [] as Array<{ id: string; fromUserId: string; toUserId: string; text: string; createdAt: string }>;
      }
    },
  });

  const selectedSohbetThread = useMemo(() => {
    const localThread = selectedSohbetMatch ? listMessages(selectedSohbetMatch.id, store) : [];
    const dbThread = (selectedSohbetDbThreadQuery.data ?? []).map((message) => ({
      id: message.id,
      matchId: selectedSohbetMatch?.id ?? "",
      fromUserId: message.fromUserId,
      text: message.text,
      createdAt: message.createdAt,
    }));

    const mergedById = new Map<string, { id: string; matchId: string; fromUserId: string; text: string; createdAt: string }>();
    const addMessage = (message: any) => {
      const explicitId = String(message?.id ?? "").trim();
      const fallbackId = `${String(message?.fromUserId ?? "")}|${String(message?.createdAt ?? "")}|${String(message?.text ?? "")}`;
      const key = explicitId || fallbackId;
      if (!key) return;
      mergedById.set(key, {
        id: explicitId || key,
        matchId: String(message?.matchId ?? selectedSohbetMatch?.id ?? ""),
        fromUserId: String(message?.fromUserId ?? ""),
        text: String(message?.text ?? ""),
        createdAt: String(message?.createdAt ?? ""),
      });
    };

    localThread.forEach(addMessage);
    dbThread.forEach(addMessage);

    return Array.from(mergedById.values())
      .filter((message) => !hiddenMessageIds[String((message as any).id ?? "")])
      .sort((a, b) => {
        const left = new Date(String(a?.createdAt ?? "")).getTime();
        const right = new Date(String(b?.createdAt ?? "")).getTime();
        if (!Number.isFinite(left) && !Number.isFinite(right)) return 0;
        if (!Number.isFinite(left)) return -1;
        if (!Number.isFinite(right)) return 1;
        return left - right;
      });
  }, [hiddenMessageIds, selectedSohbetDbThreadQuery.data, selectedSohbetMatch, store, storeVersion]);

  const sohbetSummaryQuery = useQuery({
    queryKey: ["karma-direct-summary", user?.id],
    enabled: Boolean(user?.id),
    staleTime: 3_000,
    refetchInterval: activeTab === "sohbet" ? 4000 : false,
    refetchOnWindowFocus: true,
    retry: false,
    queryFn: async () => {
      try {
        return await listDirectMessageConversationSummaries();
      } catch {
        return new Map<string, { id: string; fromUserId: string; toUserId: string; text: string; createdAt: string }>();
      }
    },
  });

  const summaryPeerIds = useMemo(() => {
    const selfIds = new Set([String(user?.id ?? "").trim(), String(realtimeMeId ?? "").trim(), String(me.id ?? "").trim()].filter(Boolean));
    return Array.from(sohbetSummaryQuery.data?.keys?.() ?? [])
      .map((id) => String(id ?? "").trim())
      .filter((id) => Boolean(id) && !selfIds.has(id));
  }, [me.id, realtimeMeId, sohbetSummaryQuery.data, user?.id]);

  const summaryPeersQuery = useQuery({
    queryKey: ["karma-direct-summary-peers", summaryPeerIds.join("|")],
    enabled: summaryPeerIds.length > 0,
    staleTime: 8_000,
    retry: (failureCount, error) => !(isFailedFetchLikeError(error)) && !isAbortLikeError(error) && failureCount < 1,
    queryFn: async () => {
      try {
        const supabase = getSupabaseClient();
        const { data, error } = await supabase
          .from("karma_profiles")
          .select("id, handle, display_name, birth_date, bio, photo_url, match_enabled, gender, preferred_gender")
          .in("id", summaryPeerIds)
          .limit(500);

        if (error) throw error;

        return ((data ?? []) as any[])
          .map((row) =>
            toKarmaUser({
              ...(row as any),
              pref_min_age: null,
              pref_max_age: null,
              pref_only_match_enabled: null,
              pref_destiny_numbers: null,
              pref_heart_desire_numbers: null,
              pref_expression_numbers: null,
              pref_karmic_debt_numbers: null,
            }),
          )
          .filter(Boolean) as KarmaUser[];
      } catch {
        return [] as KarmaUser[];
      }
    },
  });

  const summaryPeerById = useMemo(() => {
    const map = new Map<string, KarmaUser>();
    for (const peer of summaryPeersQuery.data ?? []) {
      if (!peer?.id) continue;
      map.set(String(peer.id), peer);
    }
    return map;
  }, [summaryPeersQuery.data]);

  useEffect(() => {
    if (activeTab !== "sohbet") return;
    const peerId = String(selectedSohbetPeerIdFromQuery ?? "").trim();
    if (!peerId || selectedSohbetMatchId || selectedSohbetMatch) return;

    const params = new URLSearchParams(location.search);
    const fallbackName = String(params.get("name") ?? "").trim();
    const knownPeer = getUserById(peerId, store) ?? summaryPeerById.get(peerId) ?? null;

    const ensured = ensurePrivateMatchForUser({
      peerId,
      peerDisplayName: knownPeer?.displayName || fallbackName || "Kullanıcı",
      peerHandle: knownPeer?.handle,
      peerPhotoUrl: knownPeer?.photos?.[0]?.url ?? null,
      peerBirthDate: knownPeer?.birthDate,
    });

    if (!ensured.matchId) return;

    setStoreVersion((v) => v + 1);
    navigate(`/panel/kesfet/sohbet?match=${encodeURIComponent(ensured.matchId)}&peer=${encodeURIComponent(peerId)}`, { replace: true });
  }, [
    activeTab,
    location.search,
    navigate,
    selectedSohbetMatch,
    selectedSohbetMatchId,
    selectedSohbetPeerIdFromQuery,
    store,
    summaryPeerById,
  ]);

  const selectedSohbetConversationKey = selectedSohbetConversationKeys[0] ?? "";
  const selectedSohbetSenderId = useMemo(() => {
    if (!selectedSohbetMatch) return realtimeMeId || me.id;
    if (selectedSohbetMatch.aUserId === realtimeMeId || selectedSohbetMatch.bUserId === realtimeMeId) return realtimeMeId;
    if (selectedSohbetMatch.aUserId === me.id || selectedSohbetMatch.bUserId === me.id) return me.id;
    return realtimeMeId || me.id;
  }, [me.id, realtimeMeId, selectedSohbetMatch]);
  const selectedSohbetTargetId = useMemo(() => {
    if (!selectedSohbetMatch) return selectedSohbetPeerId;
    if (selectedSohbetMatch.aUserId === selectedSohbetSenderId) return selectedSohbetMatch.bUserId;
    if (selectedSohbetMatch.bUserId === selectedSohbetSenderId) return selectedSohbetMatch.aUserId;
    return selectedSohbetPeerId;
  }, [selectedSohbetMatch, selectedSohbetPeerId, selectedSohbetSenderId]);
  const selectedSohbetTitle = useMemo(() => {
    if (!selectedSohbetOtherUser) return "Eşleşme";
    const numerology = computeKarmaNumerology(selectedSohbetOtherUser.displayName, selectedSohbetOtherUser.birthDate);
    return computeArchetype(numerology).name;
  }, [selectedSohbetOtherUser]);
  const hasSelectedSohbet = activeTab === "sohbet" && Boolean(selectedSohbetMatch && selectedSohbetOtherUser);

  const readAccessTokenFromStorage = () => {
    try {
      const parseToken = (raw: string) => {
        if (!raw) return "";
        const direct = raw.trim();
        if (direct && !direct.startsWith("{")) return direct;

        const parsed = raw ? (JSON.parse(raw) as any) : null;
        const fromRoot = typeof parsed?.access_token === "string" ? parsed.access_token : "";
        if (fromRoot) return fromRoot;
        const fromCurrentSession = typeof parsed?.currentSession?.access_token === "string" ? parsed.currentSession.access_token : "";
        if (fromCurrentSession) return fromCurrentSession;
        const fromSession = typeof parsed?.session?.access_token === "string" ? parsed.session.access_token : "";
        return fromSession || "";
      };

      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";
      const fromSupabaseStorage = storageKey ? parseToken(globalThis.localStorage?.getItem(storageKey) ?? "") : "";
      if (fromSupabaseStorage) return fromSupabaseStorage;

      const fromLegacyKey = parseToken(globalThis.localStorage?.getItem("supabase.auth.token") ?? "");
      if (fromLegacyKey) return fromLegacyKey;

      return "";
    } catch {
      return "";
    }
  };

  const safeFetch = useCallback(async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      return await fetch(input, init);
    } catch {
      return null;
    }
  }, []);

  const isBrowserOffline = typeof navigator !== "undefined" && navigator.onLine === false;

  const entitlementsQuery = useQuery({
    queryKey: ["numeroloji-membership-summary", user?.id],
    enabled: false,
    staleTime: 30_000,
    retry: false,
    initialData: () => {
      if (!user?.id) return null;
      return (queryClient.getQueryData(["numeroloji-membership-summary", user.id]) as any) ?? null;
    },
    queryFn: async () => null,
  });

  const membershipHasFeature = useMemo(() => {
    const raw = entitlementsQuery.data?.hasFeature;
    const fromApi = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    return {
      ...FREE_MEMBERSHIP_FEATURES,
      ...Object.fromEntries(Object.entries(fromApi).map(([key, value]) => [key, Boolean(value)])),
    } as Record<string, boolean>;
  }, [entitlementsQuery.data?.hasFeature]);

  const hasUnlimitedDiscovery = canChatEveryone || Boolean(membershipHasFeature.unlimited_discovery);
  const canSeeLikesYou = Boolean(membershipHasFeature.likes_you);
  const hasAdvancedFilters = canChatEveryone || Boolean(membershipHasFeature.advanced_filters);
  const hasDetailedCompatibility = canChatEveryone || Boolean(membershipHasFeature.detailed_compatibility);
  const canDirectMessageWithoutMutual = canChatEveryone || Boolean(membershipHasFeature.direct_message_without_mutual_daily_5);
  const isChatLimitedByPlan = !canChatEveryone && Boolean(membershipHasFeature.chat_limit);
  const isDiscoveryQuotaReached = !hasUnlimitedDiscovery && dailyDiscoveryUsed >= DISCOVERY_DAILY_LIMIT_FREE;

  const syncDailyDiscoveryUsage = useCallback(() => {
    if (hasUnlimitedDiscovery || !user?.id) {
      setDailyDiscoveryUsed(0);
      return;
    }

    setDailyDiscoveryUsed(getDailyDiscoveryUsage(user.id));
  }, [hasUnlimitedDiscovery, user?.id]);

  useEffect(() => {
    syncDailyDiscoveryUsage();
  }, [syncDailyDiscoveryUsage]);

  const ensureDiscoveryQuota = useCallback(() => {
    if (hasUnlimitedDiscovery) return true;
    if (!user?.id) return false;

    const consumed = tryConsumeDailyDiscovery(user.id, DISCOVERY_DAILY_LIMIT_FREE);
    setDailyDiscoveryUsed(consumed.used);

    if (consumed.allowed) return true;

    toast({
      title: "Günlük keşif hakkın doldu",
      description: `Bugün için ${DISCOVERY_DAILY_LIMIT_FREE} keşif hakkını kullandın. Sınırsız keşfet için Plus veya Pro pakete geçebilirsin.`,
      action: (
        <ToastAction altText="Planları incele" onClick={() => navigate("/panel/magaza?from=discovery-limit")}>Planları İncele</ToastAction>
      ),
    });
    navigate("/panel/magaza?from=discovery-limit");
    return false;
  }, [hasUnlimitedDiscovery, navigate, toast, user?.id]);

  useEffect(() => {
    if (!canChatEveryone || !user?.id) return;

    const todayKey = new Date().toISOString().slice(0, 10);
    const storageKey = `karma_display_name_repair_${user.id}`;
    try {
      if (globalThis.localStorage?.getItem(storageKey) === todayKey) return;
    } catch {
      // ignore
    }

    const token = readAccessTokenFromStorage();
    if (!token) return;

    const runRepair = async () => {
      if (isBrowserOffline) return;

      const res = await safeFetch("/api/profile/repair-display-names", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res || !res.ok) return;

      try {
        globalThis.localStorage?.setItem(storageKey, todayKey);
      } catch {
        // ignore
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["karma-candidates", user.id] }),
        queryClient.invalidateQueries({ queryKey: ["karma-likes-and-matches", user.id] }),
      ]);
    };

    void runRepair();
  }, [canChatEveryone, isBrowserOffline, queryClient, safeFetch, user?.id]);

  useEffect(() => {
    const syncUnread = () => setSohbetUnreadTotal(getSohbetUnreadTotal());
    syncUnread();

    const onStorage = (event: StorageEvent) => {
      if (!event.key || event.key === SOHBET_UNREAD_KEY) syncUnread();
    };

    const onUnreadChanged = () => syncUnread();

    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener("karma-sohbet-unread-changed", onUnreadChanged as EventListener);

    return () => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener("karma-sohbet-unread-changed", onUnreadChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!hasSelectedSohbet || !selectedSohbetConversationKeys.length) return;

    const unreadMap = getSohbetUnreadMap();
    let changed = false;
    selectedSohbetConversationKeys.forEach((key) => {
      if (!unreadMap[key]) return;
      unreadMap[key] = 0;
      changed = true;
    });
    if (!changed) return;

    setSohbetUnreadMap(unreadMap);
    setSohbetUnreadTotal(getSohbetUnreadTotal());
  }, [hasSelectedSohbet, selectedSohbetConversationKeys]);

  useEffect(() => {
    if (typingStopBroadcastRef.current) {
      clearTimeout(typingStopBroadcastRef.current);
      typingStopBroadcastRef.current = null;
    }
    isTypingBroadcastedRef.current = false;
    shouldAutoScrollSohbetRef.current = true;
    lastSohbetTailRef.current = "";
    setActiveMessageMenuId("");
    setEditingMessageId("");
    setEditingMessageText("");
    setShowSohbetHeaderMenu(false);
    setActiveRecentChatMenuPeerId("");
    setShowEmojiPicker(false);
  }, [selectedSohbetConversationKey]);

  useEffect(() => {
    return () => {
      if (!typingStopBroadcastRef.current) return;
      clearTimeout(typingStopBroadcastRef.current);
      typingStopBroadcastRef.current = null;
    };
  }, []);

  useEffect(() => {
    const node = sohbetThreadRef.current;
    if (!node) return;

    const updateAutoScrollState = () => {
      const distanceToBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
      shouldAutoScrollSohbetRef.current = distanceToBottom < 96;
    };

    node.addEventListener("scroll", updateAutoScrollState, { passive: true });

    return () => {
      node.removeEventListener("scroll", updateAutoScrollState);
    };
  }, [selectedSohbetConversationKey, selectedSohbetThread.length]);

  useEffect(() => {
    const node = sohbetThreadRef.current;
    if (!node) return;

    const lastMessage = selectedSohbetThread[selectedSohbetThread.length - 1] as any;
    const tailSignature = `${selectedSohbetThread.length}|${String(lastMessage?.id ?? "")}|${String(lastMessage?.createdAt ?? "")}`;
    const hasNewTail = tailSignature !== lastSohbetTailRef.current;

    if (!hasNewTail) return;
    lastSohbetTailRef.current = tailSignature;

    if (!shouldAutoScrollSohbetRef.current) return;
    node.scrollTop = node.scrollHeight;
  }, [selectedSohbetConversationKey, selectedSohbetThread]);

  const myProfileQuery = useQuery({
    queryKey: ["karma-my-profile", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) throw new Error("Oturum yok");

      try {
        return await Promise.race([
          ensureMyKarmaProfile(user.id),
          new Promise<KarmaProfileRow>((_, reject) => {
            setTimeout(() => reject(new Error("Profil isteği zaman aşımına uğradı")), 8000);
          }),
        ]);
      } catch {
        // Always return fallback profile when remote profile fetch fails.

        const meta = ((user as any)?.user_metadata ?? {}) as any;
        const first = typeof meta.first_name === "string" ? meta.first_name : "";
        const last = typeof meta.last_name === "string" ? meta.last_name : "";
        const email = typeof user.email === "string" ? String(user.email).trim() : "";
        const emailHandle = email ? email.split("@")[0] : "";
        const fallbackHandle = normalizeHandle(typeof meta.handle === "string" ? meta.handle : emailHandle) || me.handle || `user_${user.id.slice(0, 8)}`;
        const fallbackName = resolveProfileDisplayName(`${first} ${last}`.trim() || emailHandle || me.displayName, fallbackHandle);
        const fallbackPhoto = typeof meta.avatar_url === "string" ? meta.avatar_url.trim() : "";

        const yy = String(me.birthDate.year).padStart(4, "0");
        const mm = String(me.birthDate.month).padStart(2, "0");
        const dd = String(me.birthDate.day).padStart(2, "0");

        return {
          id: user.id,
          handle: fallbackHandle,
          display_name: fallbackName,
          birth_date: `${yy}-${mm}-${dd}`,
          bio: me.bio ?? "",
          photo_url: fallbackPhoto || null,
          match_enabled: true,
          gender: normalizeGender(meta.gender),
          preferred_gender: normalizePreferredGender(meta.preferred_gender),
          birth_time: null,
          birth_place: null,
          interests: null,
          pref_min_age: null,
          pref_max_age: null,
          pref_only_match_enabled: null,
          pref_destiny_numbers: [],
          pref_heart_desire_numbers: [],
          pref_expression_numbers: [],
          pref_karmic_debt_numbers: [],
        } as KarmaProfileRow;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const authMetaIdentity = useMemo(() => {
    const meta = ((user as any)?.user_metadata ?? {}) as any;
    const first = typeof meta.first_name === "string" ? meta.first_name : "";
    const last = typeof meta.last_name === "string" ? meta.last_name : "";
    const full = `${first} ${last}`.trim();
    const email = typeof (user as any)?.email === "string" ? String((user as any).email).trim() : "";
    const emailHandle = email ? email.split("@")[0] : "";
    const rawHandle = typeof meta.handle === "string" ? meta.handle : emailHandle;
    const handle = normalizeHandle(rawHandle || "");
    const displayName = resolveProfileDisplayName(full || emailHandle || "", handle);
    const avatarUrl = typeof meta.avatar_url === "string" ? meta.avatar_url.trim() : "";
    return {
      handle,
      displayName: displayName || "Kullanıcı",
      avatarUrl: avatarUrl || null,
      gender: normalizeGender(meta.gender),
      preferredGender: normalizePreferredGender(meta.preferred_gender),
    };
  }, [user]);

  const senderDisplayName = useMemo(() => {
    const profileDisplayName = resolveProfileDisplayName(String(myProfileQuery.data?.display_name ?? ""), String(myProfileQuery.data?.handle ?? ""));
    return profileDisplayName || authMetaIdentity.displayName || me.displayName;
  }, [authMetaIdentity.displayName, me.displayName, myProfileQuery.data?.display_name, myProfileQuery.data?.handle]);
  const senderHandle = useMemo(() => {
    const profileHandle = String(myProfileQuery.data?.handle ?? "").trim();
    return profileHandle || authMetaIdentity.handle || me.handle;
  }, [authMetaIdentity.handle, me.handle, myProfileQuery.data?.handle]);
  const senderPhotoUrl = useMemo(() => {
    const profilePhoto = String(myProfileQuery.data?.photo_url ?? "").trim();
    return profilePhoto || authMetaIdentity.avatarUrl || me.photos[0]?.url || null;
  }, [authMetaIdentity.avatarUrl, me.photos, myProfileQuery.data?.photo_url]);

  useEffect(() => {
    if (!user?.id) return;

    if (!myProfileQuery.data) return;

    const birthDate = parseBirthDateFromRow(myProfileQuery.data.birth_date ?? null);
    if (!birthDate) return;

    const handle =
      String(myProfileQuery.data?.handle ?? "").trim() ||
      authMetaIdentity.handle ||
      me.handle ||
      `user_${user.id.slice(0, 8)}`;
    const displayName = resolveProfileDisplayName(
      String(myProfileQuery.data?.display_name ?? "").trim() || authMetaIdentity.displayName,
      handle,
    );
    const photoUrl = String(myProfileQuery.data?.photo_url ?? authMetaIdentity.avatarUrl ?? "").trim();
    const birthDateIso = `${String(birthDate.year).padStart(4, "0")}-${String(birthDate.month).padStart(2, "0")}-${String(birthDate.day).padStart(2, "0")}`;
    const matchEnabled = typeof myProfileQuery.data?.match_enabled === "boolean" ? myProfileQuery.data.match_enabled : true;
    const gender = normalizeGender(myProfileQuery.data?.gender ?? authMetaIdentity.gender);
    const preferredGender = normalizePreferredGender(myProfileQuery.data?.preferred_gender ?? authMetaIdentity.preferredGender);

    const signature = `${user.id}|${handle}|${displayName}|${birthDateIso}|${photoUrl}|${matchEnabled}|${gender}|${preferredGender}`;
    if (profileEnsureSignatureRef.current === signature) return;

    void (async () => {
      const basePayload: Record<string, unknown> = {
        id: user.id,
        display_name: displayName,
        birth_date: birthDateIso,
        bio: myProfileQuery.data?.bio ?? "",
        match_enabled: matchEnabled,
        gender,
        preferred_gender: preferredGender,
        pref_min_age: myProfileQuery.data?.pref_min_age ?? 18,
        pref_max_age: myProfileQuery.data?.pref_max_age ?? 99,
        pref_only_match_enabled: myProfileQuery.data?.pref_only_match_enabled ?? false,
        pref_destiny_numbers: myProfileQuery.data?.pref_destiny_numbers ?? [],
        pref_heart_desire_numbers: myProfileQuery.data?.pref_heart_desire_numbers ?? [],
        pref_expression_numbers: myProfileQuery.data?.pref_expression_numbers ?? [],
        pref_karmic_debt_numbers: myProfileQuery.data?.pref_karmic_debt_numbers ?? [],
      };

      if (photoUrl) basePayload.photo_url = photoUrl;

      const handleCandidates = Array.from(
        new Set([handle, `${handle}_${user.id.slice(0, 4)}`, `${handle}_${user.id.slice(0, 8)}`].filter(Boolean)),
      );

      const supabase = getSupabaseClient();

      for (let attempt = 0; attempt < 3; attempt += 1) {
        for (const candidateHandle of handleCandidates) {
          const payload = { ...basePayload, handle: candidateHandle };
          const { error } = await supabase.from("karma_profiles").upsert(payload, { onConflict: "id" });
          if (!error) {
            profileEnsureSignatureRef.current = signature;
            void queryClient.invalidateQueries({ queryKey: ["karma-candidates", user.id] });
            void queryClient.invalidateQueries({ queryKey: ["karma-likes-and-matches", user.id] });
            return;
          }
        }

        await new Promise<void>((resolve) => {
          setTimeout(resolve, 700);
        });
      }
    })();
  }, [
    authMetaIdentity.avatarUrl,
    authMetaIdentity.displayName,
    authMetaIdentity.gender,
    authMetaIdentity.handle,
    authMetaIdentity.preferredGender,
    me.bio,
    me.birthDate,
    me.displayName,
    me.handle,
    myProfileQuery.data?.bio,
    myProfileQuery.data?.birth_date,
    myProfileQuery.data?.display_name,
    myProfileQuery.data?.handle,
    myProfileQuery.data?.match_enabled,
    myProfileQuery.data?.photo_url,
    myProfileQuery.data?.gender,
    myProfileQuery.data?.preferred_gender,
    myProfileQuery.data?.pref_destiny_numbers,
    myProfileQuery.data?.pref_expression_numbers,
    myProfileQuery.data?.pref_heart_desire_numbers,
    myProfileQuery.data?.pref_karmic_debt_numbers,
    myProfileQuery.data?.pref_max_age,
    myProfileQuery.data?.pref_min_age,
    myProfileQuery.data?.pref_only_match_enabled,
    user?.id,
  ]);

  const interactionQuery = useQuery({
    queryKey: ["karma-interactions", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) throw new Error("Oturum yok");
      try {
        return await Promise.race([
          fetchMyInteractionSets(user.id),
          new Promise<{ liked: Set<string>; passed: Set<string>; blocked: Set<string> }>((_, reject) => {
            setTimeout(() => reject(new Error("Interaction isteği zaman aşımına uğradı")), 8000);
          }),
        ]);
      } catch (_error) {
        return {
          liked: new Set<string>(),
          passed: new Set<string>(),
          blocked: new Set<string>(),
        };
      }
    },
    retry: (failureCount, error) => !(isFailedFetchLikeError(error)) && !isAbortLikeError(error) && failureCount < 2,
    staleTime: 10_000,
  });

  const candidatesQuery = useQuery({
    queryKey: ["karma-candidates", user?.id],
    enabled: !!user?.id && !!myProfileQuery.data && !!interactionQuery.data,
    queryFn: async () => {
      try {
        if (!user?.id) throw new Error("Oturum yok");
        const myProfile = myProfileQuery.data!;
        const interactions = interactionQuery.data!;

        const token = readAccessTokenFromStorage();
        const response = await safeFetch(`/api/karma/candidates${token ? "" : `?userId=${encodeURIComponent(user.id)}`}`, {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        });

        if (!response || !response.ok) {
          throw new Error("Adaylar alınamadı");
        }

        const payload = (await response.json().catch(() => null)) as any;
        const data = Array.isArray(payload?.peers) ? payload.peers : [];

        const meBirth =
          parseBirthDateFromRow(myProfile.birth_date) ??
          parseBirthDateFromMetadata((((user as any)?.user_metadata ?? {}) as Record<string, unknown>)) ??
          me.birthDate;
        const meBirthForCalc = meBirth ?? { day: 1, month: 1, year: 2000 };

        const meNumerology = computeKarmaNumerology(myProfile.display_name, meBirthForCalc);

        const mappedList = (data ?? [])
          .map((row: any) => {
            const id = String(row.id);
            if (interactions.passed.has(id) || interactions.blocked.has(id) || interactions.liked.has(id)) return null;

            const mapped = toKarmaUser({
              ...(row as any),
              pref_min_age: null,
              pref_max_age: null,
              pref_only_match_enabled: null,
              pref_destiny_numbers: null,
              pref_heart_desire_numbers: null,
              pref_expression_numbers: null,
              pref_karmic_debt_numbers: null,
            });

            if (!mapped) return null;

            const nu = computeKarmaNumerology(mapped.displayName, mapped.birthDate);
            const overview = computeMatchOverview(meNumerology, nu);

            return {
              user: mapped,
              overview: personalizeMatchOverview(overview, {
                meName: myProfile.display_name,
                otherName: mapped.displayName,
              }),
            };
          })
          .filter(Boolean) as Array<{ user: KarmaUser; overview: ReturnType<typeof computeMatchOverview> }>;

        const peerIds = mappedList.map((item) => String(item.user.id ?? "").trim()).filter(Boolean);
        const publicProgressByUser = new Map<string, { totalXp: number; journeyBadges: Array<{ key: string; label: string; imageUrl?: string; short?: string }> }>();

        if (peerIds.length) {
          const progressResponse = await safeFetch(
            `/api/numeroloji/progress/public?ids=${encodeURIComponent(peerIds.join(","))}`,
            {
              headers: token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : undefined,
            },
          );

          if (progressResponse?.ok) {
            const progressPayload = (await progressResponse.json().catch(() => null)) as any;
            const items = Array.isArray(progressPayload?.items) ? progressPayload.items : [];
            for (const item of items) {
              const key = String(item?.userId ?? "").trim();
              if (!key) continue;
              publicProgressByUser.set(key, {
                totalXp: Math.max(0, Number(item?.totalXp ?? 0) || 0),
                journeyBadges: Array.isArray(item?.journeyBadges)
                  ? item.journeyBadges
                      .map((badge: any) => ({
                        key: String(badge?.key ?? "").trim(),
                        label: String(badge?.label ?? "").trim(),
                        imageUrl: String(badge?.imageUrl ?? "").trim() || undefined,
                        short: String(badge?.short ?? "").trim() || undefined,
                      }))
                      .filter((badge: any) => badge.key && badge.label)
                  : [],
              });
            }
          }
        }

        return mappedList
          .map((entry) => {
            const progress = publicProgressByUser.get(entry.user.id);
            return {
              ...entry,
              user: {
                ...entry.user,
                interests: Array.isArray(entry.user.interests) ? entry.user.interests : [],
                journeyBadges: progress?.journeyBadges,
                reputation: progress?.totalXp ?? entry.user.reputation,
              },
            };
          })
          .sort((a: any, b: any) => b.overview.scores.dailyHarmony - a.overview.scores.dailyHarmony);
      } catch (_error) {
        return [] as Array<{ user: KarmaUser; overview: any }>;
      }
    },
    retry: (failureCount, error) => !(isFailedFetchLikeError(error)) && !isAbortLikeError(error) && failureCount < 2,
    staleTime: 10_000,
  });

  const authoritativeMutualMatchesQuery = useQuery({
    queryKey: ["karma-sohbet-mutuals-authoritative-v3", user?.id],
    enabled: !!user?.id && activeTab === "sohbet",
    staleTime: 15_000,
    retry: 1,
    queryFn: async () => {
      if (!user?.id || isBrowserOffline) return [] as Array<{ user: KarmaUser; overview: ReturnType<typeof buildFastSohbetOverview> }>;
      const token = readAccessTokenFromStorage();

      const response = await safeFetch(`/api/karma/mutuals${token ? "" : `?userId=${encodeURIComponent(user.id)}`}`, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });
      if (!response || !response.ok) return [] as Array<{ user: KarmaUser; overview: ReturnType<typeof buildFastSohbetOverview> }>;

      const payload = (await response.json().catch(() => null)) as any;
      const peers = Array.isArray(payload?.peers) ? payload.peers : [];

      return peers
        .map((row: any) =>
          toKarmaUser({
            ...(row as any),
            pref_min_age: null,
            pref_max_age: null,
            pref_only_match_enabled: null,
            pref_destiny_numbers: null,
            pref_heart_desire_numbers: null,
            pref_expression_numbers: null,
            pref_karmic_debt_numbers: null,
          }),
        )
        .filter(Boolean)
        .map((peer: any) => ({
          user: peer,
          overview: buildFastSohbetOverview(peer),
        })) as Array<{ user: KarmaUser; overview: ReturnType<typeof buildFastSohbetOverview> }>;
    },
  });

  const likesAndMatchesQuery = useQuery({
    queryKey: ["karma-likes-and-matches", user?.id, myProfileQuery.data?.id, canChatEveryone],
    enabled: !!user?.id && !!myProfileQuery.data,
    queryFn: async () => {
      try {
        if (!user?.id) throw new Error("Oturum yok");
        const myProfile = myProfileQuery.data!;
        const meBirth =
          parseBirthDateFromRow(myProfile.birth_date) ??
          parseBirthDateFromMetadata((((user as any)?.user_metadata ?? {}) as Record<string, unknown>)) ??
          me.birthDate;
        const meBirthForCalc = meBirth ?? { day: 1, month: 1, year: 2000 };

        const supabase = getSupabaseClient();
        const [receivedRes, givenRes, blocksRes, passesRes] = await Promise.all([
          supabase.from("karma_likes").select("from_user_id").eq("to_user_id", user.id).limit(500),
          supabase.from("karma_likes").select("to_user_id").eq("from_user_id", user.id).limit(500),
          supabase.from("karma_blocks").select("to_user_id").eq("from_user_id", user.id).limit(500),
          supabase.from("karma_passes").select("to_user_id").eq("from_user_id", user.id).limit(500),
        ]);

        if (receivedRes.error) throw receivedRes.error;
        if (givenRes.error) throw givenRes.error;
        if (blocksRes.error) throw blocksRes.error;
        if (passesRes.error) throw passesRes.error;

        const blockedIds = new Set((blocksRes.data ?? []).map((r: any) => String(r.to_user_id)).filter(Boolean));
        const hiddenIds = new Set([...blockedIds]);
        const receivedIds = Array.from(new Set((receivedRes.data ?? []).map((r: any) => String(r.from_user_id)).filter((id: string) => Boolean(id) && !hiddenIds.has(id))));
        const givenIds = Array.from(new Set((givenRes.data ?? []).map((r: any) => String(r.to_user_id)).filter((id: string) => Boolean(id) && !hiddenIds.has(id))));
        const givenSet = new Set(givenIds);
        const mutualIds = receivedIds.filter((id) => givenSet.has(id));
        const allIds = Array.from(new Set([...receivedIds, ...givenIds, ...mutualIds]));

        let sohbetIds = mutualIds;
        let profileRows: any[] = [];

        if (canChatEveryone) {
          const { data: everyoneRows, error: everyoneErr } = await supabase
            .from("karma_profiles")
            .select("id, handle, display_name, birth_date, bio, photo_url, match_enabled, gender")
            .eq("match_enabled", true)
            .neq("id", user.id)
            .limit(500);
          if (everyoneErr) throw everyoneErr;
          profileRows = (everyoneRows ?? []) as any[];
          sohbetIds = profileRows.map((row: any) => String(row.id)).filter((id) => Boolean(id) && !hiddenIds.has(id));
        } else {
          if (!allIds.length) return { received: [], given: [], mutual: [], sohbet: [] } as { received: any[]; given: any[]; mutual: any[]; sohbet: any[] };
          const { data: likesBasedRows, error: profileErr } = await supabase
            .from("karma_profiles")
            .select("id, handle, display_name, birth_date, bio, photo_url, match_enabled, gender")
            .in("id", allIds);
          if (profileErr) throw profileErr;
          profileRows = (likesBasedRows ?? []) as any[];
        }

        const meNumerology = computeKarmaNumerology(myProfile.display_name, meBirthForCalc);
        const mappedById = new Map<string, { user: KarmaUser; overview: ReturnType<typeof computeMatchOverview> }>();

        for (const row of profileRows ?? []) {
          const mapped = toKarmaUser({
            ...(row as any),
            pref_min_age: null,
            pref_max_age: null,
            pref_only_match_enabled: null,
            pref_destiny_numbers: null,
            pref_heart_desire_numbers: null,
            pref_expression_numbers: null,
            pref_karmic_debt_numbers: null,
          });
          if (!mapped) continue;

          const nu = computeKarmaNumerology(mapped.displayName, mapped.birthDate);
          mappedById.set(mapped.id, {
            user: mapped,
            overview: personalizeMatchOverview(computeMatchOverview(meNumerology, nu), {
              meName: myProfile.display_name,
              otherName: mapped.displayName,
            }),
          });
        }

        const pick = (ids: string[]) =>
          ids
            .map((id) => mappedById.get(id))
            .filter(Boolean)
            .sort((a: any, b: any) => b.overview.scores.dailyHarmony - a.overview.scores.dailyHarmony);

        return {
          received: pick(receivedIds),
          given: pick(givenIds),
          mutual: pick(mutualIds),
          sohbet: pick(sohbetIds),
        };
      } catch (error) {
        try {
          if (user?.id) {
            const supabase = getSupabaseClient();
            const [receivedRes, givenRes] = await Promise.all([
              supabase.from("karma_likes").select("from_user_id").eq("to_user_id", user.id).limit(500),
              supabase.from("karma_likes").select("to_user_id").eq("from_user_id", user.id).limit(500),
            ]);

            if (receivedRes.error) throw receivedRes.error;
            if (givenRes.error) throw givenRes.error;

            const receivedIds = Array.from(new Set((receivedRes.data ?? []).map((r: any) => String(r.from_user_id)).filter(Boolean)));
            const givenIds = Array.from(new Set((givenRes.data ?? []).map((r: any) => String(r.to_user_id)).filter(Boolean)));
            const givenSet = new Set(givenIds);
            const mutualIds = receivedIds.filter((id) => givenSet.has(id));
            const allIds = Array.from(new Set([...receivedIds, ...givenIds]));

            if (!allIds.length) {
              return { received: [], given: [], mutual: [], sohbet: [] } as { received: any[]; given: any[]; mutual: any[]; sohbet: any[] };
            }

            const { data: profileRows, error: profileErr } = await supabase
              .from("karma_profiles")
              .select("id, handle, display_name, birth_date, bio, photo_url, match_enabled, gender")
              .in("id", allIds);

            if (profileErr) throw profileErr;

            const mappedById = new Map<string, { user: KarmaUser; overview: ReturnType<typeof buildFastSohbetOverview> }>();

            for (const row of profileRows ?? []) {
              const mapped = toKarmaUser({
                ...(row as any),
                pref_min_age: null,
                pref_max_age: null,
                pref_only_match_enabled: null,
                pref_destiny_numbers: null,
                pref_heart_desire_numbers: null,
                pref_expression_numbers: null,
                pref_karmic_debt_numbers: null,
              });
              if (!mapped) continue;

              mappedById.set(mapped.id, {
                user: mapped,
                overview: buildFastSohbetOverview(mapped),
              });
            }

            const pick = (ids: string[]) =>
              ids
                .map((id) => mappedById.get(id))
                .filter(Boolean)
                .sort((a: any, b: any) => Number(b?.overview?.scores?.dailyHarmony ?? 0) - Number(a?.overview?.scores?.dailyHarmony ?? 0));

            return {
              received: pick(receivedIds),
              given: pick(givenIds),
              mutual: pick(mutualIds),
              sohbet: pick(mutualIds),
            } as { received: any[]; given: any[]; mutual: any[]; sohbet: any[] };
          }
        } catch (_rescueError) {
          // no-op: son çare local fallback'a düş.
        }

        const fallbackMyName =
          resolveProfileDisplayName(String(myProfileQuery.data?.display_name ?? ""), String(myProfileQuery.data?.handle ?? "")) ||
          me.displayName ||
          "Sen";
        const fallbackMyBirth = parseBirthDateFromRow(myProfileQuery.data?.birth_date ?? null) ?? me.birthDate;
        const fallbackMyNumerology = computeKarmaNumerology(fallbackMyName, fallbackMyBirth);
        const selfIds = new Set([String(user?.id ?? "").trim(), String(me.id ?? "").trim()].filter(Boolean));
        const mergedByPeerId = new Map<string, { user: KarmaUser; overview: ReturnType<typeof computeMatchOverview> }>();

        for (const match of store.matches ?? []) {
          const aUserId = String((match as any)?.aUserId ?? "").trim();
          const bUserId = String((match as any)?.bUserId ?? "").trim();
          if (!aUserId || !bUserId) continue;
          const peerId = selfIds.has(aUserId) ? bUserId : selfIds.has(bUserId) ? aUserId : "";
          if (!peerId || mergedByPeerId.has(peerId)) continue;

          const peer = getUserById(peerId, store);
          if (!peer) continue;

          const peerNumerology = computeKarmaNumerology(peer.displayName, peer.birthDate);
          mergedByPeerId.set(peerId, {
            user: peer,
            overview: personalizeMatchOverview(computeMatchOverview(fallbackMyNumerology, peerNumerology), {
              meName: fallbackMyName,
              otherName: peer.displayName,
            }),
          });
        }

        let addedFromStore = 0;
        for (const peer of (store.users ?? []) as KarmaUser[]) {
          if (addedFromStore >= 120) break;
          if (!peer?.id || selfIds.has(peer.id) || mergedByPeerId.has(peer.id) || !peer.matchEnabled) continue;
          const peerNumerology = computeKarmaNumerology(peer.displayName, peer.birthDate);
          mergedByPeerId.set(peer.id, {
            user: peer,
            overview: personalizeMatchOverview(computeMatchOverview(fallbackMyNumerology, peerNumerology), {
              meName: fallbackMyName,
              otherName: peer.displayName,
            }),
          });
          addedFromStore += 1;
        }

        const fallbackSohbet = Array.from(mergedByPeerId.values()).sort((a: any, b: any) => {
          const left = Number(a?.overview?.scores?.dailyHarmony ?? 0);
          const right = Number(b?.overview?.scores?.dailyHarmony ?? 0);
          return right - left;
        });

        if (isFailedFetchLikeError(error) || isAbortLikeError(error)) {
          return { received: [], given: [], mutual: [], sohbet: fallbackSohbet } as { received: any[]; given: any[]; mutual: any[]; sohbet: any[] };
        }

        return { received: [], given: [], mutual: [], sohbet: fallbackSohbet } as { received: any[]; given: any[]; mutual: any[]; sohbet: any[] };
      }
    },
    retry: (failureCount, error) => !(isFailedFetchLikeError(error)) && !isAbortLikeError(error) && failureCount < 2,
    staleTime: 8_000,
  });

  useEffect(() => {
    if (!user?.id || !myProfileQuery.data) return;

    const profile = myProfileQuery.data;
    const birthDate = parseBirthDateFromRow(profile.birth_date ?? null);
    if (!birthDate) return;

    const displayName = resolveProfileDisplayName(String(profile.display_name ?? ""), String(profile.handle ?? ""));
    const nextHandle = String(profile.handle ?? "").trim();
    const nextPhoto = String(profile.photo_url ?? "").trim();

    const mePhoto = String(me.photos[0]?.url ?? "").trim();
    const profileLooksSynced =
      me.id === user.id &&
      me.displayName.trim() === displayName &&
      me.handle.trim() === nextHandle &&
      mePhoto === nextPhoto &&
      me.birthDate.day === birthDate.day &&
      me.birthDate.month === birthDate.month &&
      me.birthDate.year === birthDate.year;

    if (profileLooksSynced) return;

    setMe(user.id, {
      handle: nextHandle,
      displayName,
      bio: profile.bio ?? "",
      photoUrl: profile.photo_url ?? null,
      birthDate,
    });
    setStoreVersion((v) => v + 1);
  }, [me.birthDate.day, me.birthDate.month, me.birthDate.year, me.displayName, me.handle, me.id, me.photos, myProfileQuery.data, user?.id]);

  const compatibilityTargetQuery = useQuery({
    queryKey: ["karma-compatibility-target", user?.id, targetUserId],
    enabled: (activeTab === "uyum" || activeTab === "profil") && !!user?.id && !!targetUserId,
    queryFn: async () => {
      try {
        const supabase = getSupabaseClient();
        const baseSelect = "id, handle, display_name, birth_date, bio, photo_url, match_enabled";
        const extrasSelect = `${baseSelect}, birth_time, birth_place, interests`;

        let data: any = null;
        let error: any = null;

        const withExtras = await supabase.from("karma_profiles").select(extrasSelect).eq("id", targetUserId).maybeSingle();
        data = withExtras.data;
        error = withExtras.error;

        const msg = String(error?.message ?? "");
        if (error && msg.includes("column") && (msg.includes("birth_time") || msg.includes("birth_place") || msg.includes("interests"))) {
          const fallback = await supabase.from("karma_profiles").select(baseSelect).eq("id", targetUserId).maybeSingle();
          data = fallback.data;
          error = fallback.error;
        }

        if (error || !data) {
          const fallbackUser = getUserById(targetUserId, store);
          if (!fallbackUser) return null;

          const yy = String(fallbackUser.birthDate.year).padStart(4, "0");
          const mm = String(fallbackUser.birthDate.month).padStart(2, "0");
          const dd = String(fallbackUser.birthDate.day).padStart(2, "0");

          return {
            id: fallbackUser.id,
            handle: fallbackUser.handle,
            display_name: fallbackUser.displayName,
            birth_date: `${yy}-${mm}-${dd}`,
            bio: fallbackUser.bio ?? "",
            photo_url: fallbackUser.photos[0]?.url ?? null,
            match_enabled: Boolean(fallbackUser.matchEnabled),
            birth_time: null,
            birth_place: null,
            interests: null,
            pref_min_age: null,
            pref_max_age: null,
            pref_only_match_enabled: null,
            pref_destiny_numbers: null,
            pref_heart_desire_numbers: null,
            pref_expression_numbers: null,
            pref_karmic_debt_numbers: null,
          } as KarmaProfileRow;
        }

        return {
          ...data,
          birth_time: typeof data.birth_time === "string" ? data.birth_time : null,
          birth_place: typeof data.birth_place === "string" ? data.birth_place : null,
          interests: Array.isArray(data.interests) ? data.interests.filter((v: unknown) => typeof v === "string") : null,
        } as KarmaProfileRow;
      } catch {
        const fallbackUser = getUserById(targetUserId, store);
        if (!fallbackUser) return null;

        const yy = String(fallbackUser.birthDate.year).padStart(4, "0");
        const mm = String(fallbackUser.birthDate.month).padStart(2, "0");
        const dd = String(fallbackUser.birthDate.day).padStart(2, "0");

        return {
          id: fallbackUser.id,
          handle: fallbackUser.handle,
          display_name: fallbackUser.displayName,
          birth_date: `${yy}-${mm}-${dd}`,
          bio: fallbackUser.bio ?? "",
          photo_url: fallbackUser.photos[0]?.url ?? null,
          match_enabled: Boolean(fallbackUser.matchEnabled),
          birth_time: null,
          birth_place: null,
          interests: null,
          pref_min_age: null,
          pref_max_age: null,
          pref_only_match_enabled: null,
          pref_destiny_numbers: null,
          pref_heart_desire_numbers: null,
          pref_expression_numbers: null,
          pref_karmic_debt_numbers: null,
        } as KarmaProfileRow;
      }
    },
    retry: (failureCount, error) => !(isFailedFetchLikeError(error)) && !isAbortLikeError(error) && failureCount < 2,
    staleTime: 15_000,
  });

  const profilePortraitQuery = useQuery({
    queryKey: ["kesfet-profile-portrait", targetUserId, compatibilityTargetQuery.data?.display_name, compatibilityTargetQuery.data?.birth_date],
    enabled:
      activeTab === "profil" &&
      !!targetUserId &&
      !!compatibilityTargetQuery.data?.display_name &&
      !!compatibilityTargetQuery.data?.birth_date,
    queryFn: async () => {
      const row = compatibilityTargetQuery.data;
      const birthDate = parseBirthDateFromRow(row?.birth_date ?? null);
      if (!row || !birthDate || isBrowserOffline) return null;

      const parts = row.display_name.trim().split(" ").filter(Boolean);
      const firstName = parts[0] ?? "";
      const lastName = parts.slice(1).join(" ");

      const response = await safeFetch("/api/numeroloji/portrait-insights", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName,
          lastName,
          day: birthDate.day,
          month: birthDate.month,
          year: birthDate.year,
          lang: "tr",
        }),
      });

      if (!response || !response.ok) return null;
      const data = (await response.json().catch(() => null)) as any;
      if (!data?.ok) return null;

      return {
        destiny: data?.texts?.destiny,
        expression: data?.texts?.expression,
        heart_desire: data?.texts?.heart_desire,
        personality: data?.texts?.personality,
        birth_day: data?.texts?.birth_day,
        karmic_dept: data?.texts?.karmic_dept,
      } as {
        destiny?: string;
        expression?: string;
        heart_desire?: string;
        personality?: string;
        birth_day?: string;
        karmic_dept?: string;
      } | null;
    },
    staleTime: 60_000,
  });

  const compatibilityDetail = useMemo(() => {
    if (activeTab !== "uyum") return null;
    const me = myProfileQuery.data;
    const targetRow = compatibilityTargetQuery.data;
    if (!me || !targetRow) return null;

    const myBirthDate = parseBirthDateFromRow(me.birth_date);
    const targetUser = toKarmaUser({
      ...targetRow,
      pref_min_age: null,
      pref_max_age: null,
      pref_only_match_enabled: null,
      pref_destiny_numbers: null,
      pref_heart_desire_numbers: null,
      pref_expression_numbers: null,
      pref_karmic_debt_numbers: null,
    });

    if (!myBirthDate || !targetUser) return null;

    const myNumerology = computeKarmaNumerology(me.display_name, myBirthDate);
    const targetNumerology = computeKarmaNumerology(targetUser.displayName, targetUser.birthDate);
    const overview = personalizeMatchOverview(computeMatchOverview(myNumerology, targetNumerology), {
      meName: me.display_name,
      otherName: targetUser.displayName,
    });

    const lifePathHarmony = Math.max(35, 100 - Math.abs(myNumerology.destinyNumber - targetNumerology.destinyNumber) * 11);
    const soulHarmony = Math.max(35, 100 - Math.abs(myNumerology.heartDesireNumber - targetNumerology.heartDesireNumber) * 13);

    return {
      me: {
        name: me.display_name,
        photo: me.photo_url || "/placeholder.svg",
        destiny: myNumerology.destinyNumber,
        soul: myNumerology.heartDesireNumber,
        expression: myNumerology.expressionNumber,
        age: getAge(myBirthDate),
      },
      target: {
        name: targetUser.displayName,
        photo: getPrimaryPhotoUrl(targetUser),
        destiny: targetNumerology.destinyNumber,
        soul: targetNumerology.heartDesireNumber,
        expression: targetNumerology.expressionNumber,
        age: getAge(targetUser.birthDate),
      },
      targetUser,
      overview,
      scores: {
        lifePathHarmony,
        soulHarmony,
        spiritual: Math.min(99, Math.max(40, Math.round((overview.scores.dailyHarmony + overview.scores.longevity) / 2))),
        emotional: overview.scores.attraction,
        mental: overview.scores.longevity,
        physical: Math.max(45, Math.round((overview.scores.attraction * 0.7 + overview.scores.dailyHarmony * 0.3))),
        cosmic: Math.min(99, Math.max(40, overview.relationshipNumber * 11)),
      },
    };
  }, [activeTab, myProfileQuery.data, compatibilityTargetQuery.data]);

  const profileDetail = useMemo(() => {
    if (activeTab !== "profil") return null;
    const targetRow = compatibilityTargetQuery.data;
    if (!targetRow) return null;

    const targetUser = toKarmaUser({
      ...targetRow,
      pref_min_age: null,
      pref_max_age: null,
      pref_only_match_enabled: null,
      pref_destiny_numbers: null,
      pref_heart_desire_numbers: null,
      pref_expression_numbers: null,
      pref_karmic_debt_numbers: null,
    });
    if (!targetUser) return null;

    const targetNumerology = computeKarmaNumerology(targetUser.displayName, targetUser.birthDate);
    const targetArchetype = computeArchetype(targetNumerology);
    const targetTheme = getDestinyTheme(targetNumerology.destinyNumber);
    const insights = getProfileInsights(targetNumerology);
    const insightById = new Map(insights.map((insight) => [insight.id, insight.body]));
    const uniqueKarmicCodes = Array.from(new Set(targetNumerology.karmicCodes));
    const templateCopy = profilePortraitQuery.data;

    const pickSummary = (templateValue: unknown, insightId: string) => {
      if (hasMeaningfulText(templateValue)) return normalizePublicInsight(templateValue);
      return normalizePublicInsight(insightById.get(insightId) ?? "");
    };

    const karmicFromTemplate = hasMeaningfulText(templateCopy?.karmic_dept) ? normalizePublicInsight(templateCopy.karmic_dept) : "";
    const photos = targetUser.photos.map((photo) => String(photo?.url ?? "").trim()).filter(Boolean).slice(0, 5);

    const journeyBadges = [
      { key: "life-path", label: `Yaşam Yolu ${targetNumerology.destinyNumber}`, short: String(targetNumerology.destinyNumber) },
      { key: "heart", label: `Kalp Arzusu ${targetNumerology.heartDesireNumber}`, short: String(targetNumerology.heartDesireNumber) },
      { key: "expression", label: `İfade ${targetNumerology.expressionNumber}`, short: String(targetNumerology.expressionNumber) },
      { key: "archetype", label: targetArchetype.name, short: targetArchetype.name.slice(0, 2).toLocaleUpperCase("tr-TR") },
    ];

    return {
      user: targetUser,
      numerology: targetNumerology,
      archetype: targetArchetype,
      theme: targetTheme,
      age: getAge(targetUser.birthDate),
      photos,
      journeyBadges,
      interests: Array.isArray(targetRow.interests) ? targetRow.interests.filter((v) => typeof v === "string") : [],
      quickSummary: `${targetArchetype.headline} Öne çıkan tarafları: ${targetArchetype.strengths}.`,
      archetypeSpotlight: `${targetArchetype.name} arketipi, ${targetArchetype.headline.toLocaleLowerCase("tr-TR")} Bu profil; ${targetArchetype.strengths} taraflarıyla çevresine güven ve ilham verir.`,
      destinyInsight: pickSummary(templateCopy?.destiny, "destiny"),
      heartInsight: pickSummary(templateCopy?.heart_desire, "heart"),
      expressionInsight: pickSummary(templateCopy?.expression, "expression"),
      personalityInsight: pickSummary(templateCopy?.personality, "personality"),
      birthdayInsight: pickSummary(templateCopy?.birth_day, "birthday"),
      karmicInsights: karmicFromTemplate
        ? [karmicFromTemplate]
        : uniqueKarmicCodes.map((code) => `Karmik Kod ${code}: ${getShopBundleForCode(code).headline}`),
    };
  }, [activeTab, compatibilityTargetQuery.data, profilePortraitQuery.data]);

  const shown = useMemo(() => {
    const dismissed = new Set(dismissedIds);
    const minAge = Math.max(18, Math.min(99, Math.trunc(discoveryMinAge) || 18));
    const maxAge = Math.max(minAge, Math.min(99, Math.trunc(discoveryMaxAge) || minAge));

    return (candidatesQuery.data ?? []).filter((c: any) => {
      if (!c || dismissed.has(c.user.id)) return false;

      const age = getAge(c.user.birthDate);
      if (age < minAge || age > maxAge) return false;

      const candidateGender = normalizeGender(c.user.gender);
      if (discoveryGender !== "herkes" && candidateGender !== discoveryGender) return false;

      if (discoveryDestinyNumber > 0 || discoveryHeartNumber > 0 || discoveryExpressionNumber > 0) {
        const numerology = computeKarmaNumerology(c.user.displayName, c.user.birthDate);
        if (discoveryDestinyNumber > 0 && numerology.destinyNumber !== discoveryDestinyNumber) return false;
        if (discoveryHeartNumber > 0 && numerology.heartDesireNumber !== discoveryHeartNumber) return false;
        if (discoveryExpressionNumber > 0 && numerology.expressionNumber !== discoveryExpressionNumber) return false;
      }

      return true;
    });
  }, [candidatesQuery.data, dismissedIds, discoveryDestinyNumber, discoveryExpressionNumber, discoveryGender, discoveryHeartNumber, discoveryMaxAge, discoveryMinAge]);

  const current = shown.length ? shown[cursor % shown.length] : null;

  const topPicks = useMemo(() => {
    const base = shown.slice(0, 12);
    return current ? base.filter((c: any) => c.user.id !== current.user.id).slice(0, 6) : base.slice(0, 6);
  }, [shown, current]);

  const likesReceived = likesAndMatchesQuery.data?.received ?? [];
  const likesGiven = likesAndMatchesQuery.data?.given ?? [];
  const mutualLikes = likesAndMatchesQuery.data?.mutual ?? [];
  const blockedUserIds = useMemo(() => new Set(Array.from(interactionQuery.data?.blocked ?? [])), [interactionQuery.data?.blocked]);
  const passedUserIds = useMemo(() => new Set(Array.from(interactionQuery.data?.passed ?? [])), [interactionQuery.data?.passed]);
  const likesGivenUserIds = useMemo(() => new Set(likesGiven.map((item: any) => item.user.id)), [likesGiven]);
  const mutualLikeUserIds = useMemo(() => new Set(mutualLikes.map((item: any) => item.user.id)), [mutualLikes]);
  const receivedUnmatchedLikes = useMemo(
    () => likesReceived.filter((item: any) => !mutualLikeUserIds.has(String(item?.user?.id ?? ""))),
    [likesReceived, mutualLikeUserIds],
  );

  useEffect(() => {
    if (activeTab !== "sohbet") return;
    if (!selectedSohbetPeerId) return;
    if (!passedUserIds.has(selectedSohbetPeerId)) return;

    navigate("/panel/kesfet/sohbet");
    toast({
      title: "Bu kişi geçildi",
      description: "Geç dediğin kişilerle sohbet kapanır.",
    });
  }, [activeTab, navigate, passedUserIds, selectedSohbetPeerId, toast]);
  const compatibilityTargetId = compatibilityDetail?.targetUser.id ?? "";
  const compatibilityLikeOverrideValue = compatibilityTargetId ? compatibilityLikeOverride[compatibilityTargetId] : undefined;
  const isCompatibilityLiked = useMemo(() => {
    if (!compatibilityTargetId) return false;
    if (typeof compatibilityLikeOverrideValue === "boolean") return compatibilityLikeOverrideValue;
    return likesGivenUserIds.has(compatibilityTargetId);
  }, [compatibilityLikeOverrideValue, compatibilityTargetId, likesGivenUserIds]);

  useEffect(() => {
    if (!compatibilityTargetId || typeof compatibilityLikeOverrideValue !== "boolean") return;
    const serverLiked = likesGivenUserIds.has(compatibilityTargetId);
    if (serverLiked !== compatibilityLikeOverrideValue) return;

    setCompatibilityLikeOverride((prev) => {
      if (!(compatibilityTargetId in prev)) return prev;
      const next = { ...prev };
      delete next[compatibilityTargetId];
      return next;
    });
  }, [compatibilityLikeOverrideValue, compatibilityTargetId, likesGivenUserIds]);

  const sohbetList = useMemo(() => {
    const fromAuthoritative = (authoritativeMutualMatchesQuery.data ?? []) as any[];
    const fromServerMutual = (likesAndMatchesQuery.data?.mutual ?? []) as any[];
    const mergedByPeerId = new Map<string, any>();

    for (const entry of fromAuthoritative) {
      const peerId = String(entry?.user?.id ?? "").trim();
      if (!peerId) continue;
      mergedByPeerId.set(peerId, entry);
    }

    for (const entry of fromServerMutual) {
      const peerId = String(entry?.user?.id ?? "").trim();
      if (!peerId || mergedByPeerId.has(peerId)) continue;
      mergedByPeerId.set(peerId, entry);
    }

    if (!mergedByPeerId.size) {
      const selfIds = new Set([String(user?.id ?? "").trim(), String(realtimeMeId ?? "").trim(), String(me.id ?? "").trim()].filter(Boolean));
      for (const match of store.matches ?? []) {
        const aUserId = String((match as any)?.aUserId ?? "").trim();
        const bUserId = String((match as any)?.bUserId ?? "").trim();
        if (!aUserId || !bUserId) continue;

        const peerId = selfIds.has(aUserId) ? bUserId : selfIds.has(bUserId) ? aUserId : "";
        if (!peerId || mergedByPeerId.has(peerId)) continue;

        const peer = getUserById(peerId, store);
        if (!peer) continue;

        mergedByPeerId.set(peerId, {
          user: peer,
          overview: buildFastSohbetOverview(peer),
        });
      }
    }

    const myName =
      resolveProfileDisplayName(String(myProfileQuery.data?.display_name ?? ""), String(myProfileQuery.data?.handle ?? "")) ||
      me.displayName ||
      "Sen";
    const myBirthDate = parseBirthDateFromRow(myProfileQuery.data?.birth_date ?? null) ?? me.birthDate;
    const myNumerology = computeKarmaNumerology(myName, myBirthDate);

    return Array.from(mergedByPeerId.values())
      .map((entry: any) => {
        const peer = entry?.user as KarmaUser | undefined;
        if (!peer?.displayName || !peer?.birthDate) return entry;

        const peerNumerology = computeKarmaNumerology(peer.displayName, peer.birthDate);
        const overview = personalizeMatchOverview(computeMatchOverview(myNumerology, peerNumerology), {
          meName: myName,
          otherName: peer.displayName,
        });

        return {
          ...entry,
          overview,
        };
      })
      .sort((a: any, b: any) => {
        const left = Number(a?.overview?.scores?.dailyHarmony ?? 0);
        const right = Number(b?.overview?.scores?.dailyHarmony ?? 0);
        return right - left;
      });
  }, [
    authoritativeMutualMatchesQuery.data,
    likesAndMatchesQuery.data?.mutual,
    me.birthDate,
    me.displayName,
    me.id,
    myProfileQuery.data?.birth_date,
    myProfileQuery.data?.display_name,
    myProfileQuery.data?.handle,
    realtimeMeId,
    store,
    user?.id,
  ]);
  const sohbetUserIds = useMemo(() => new Set(sohbetList.map((item: any) => item.user.id)), [sohbetList]);
  const likesCards = receivedUnmatchedLikes;
  const shouldBlurReceivedLikes = !canSeeLikesYou;
  const displayedLikesCards = useMemo(() => {
    const filtered = !hasAdvancedFilters
      ? likesCards
      : likesCards.filter((item: any) => {
          const harmony = Number(item?.overview?.scores?.dailyHarmony ?? 0);
          if (harmony < likesMinHarmony) return false;

          if (likesOnlyWithPhoto) {
            const hasPhoto = Boolean(item?.user?.photos?.[0]?.url || item?.user?.photoUrl);
            if (!hasPhoto) return false;
          }

          return true;
        });

    return filtered
      .slice()
      .sort((a: any, b: any) => {
        if (likesSortBy === "harmony") {
          const left = Number(a?.overview?.scores?.dailyHarmony ?? 0);
          const right = Number(b?.overview?.scores?.dailyHarmony ?? 0);
          return right - left;
        }

        if (likesSortBy === "name") {
          const left = String(a?.user?.displayName ?? "");
          const right = String(b?.user?.displayName ?? "");
          return left.localeCompare(right, "tr");
        }

        const leftRaw = String(a?.createdAt ?? a?.like?.createdAt ?? "").trim();
        const rightRaw = String(b?.createdAt ?? b?.like?.createdAt ?? "").trim();
        const leftTs = leftRaw ? new Date(leftRaw).getTime() : 0;
        const rightTs = rightRaw ? new Date(rightRaw).getTime() : 0;
        return rightTs - leftTs;
      });
  }, [hasAdvancedFilters, likesCards, likesMinHarmony, likesOnlyWithPhoto, likesSortBy]);

  const filteredSohbetList = useMemo(() => {
    const mergedByPeerId = new Map<string, any>();

    for (const item of sohbetList) {
      const peerId = String(item?.user?.id ?? "").trim();
      if (!peerId || blockedUserIds.has(peerId) || passedUserIds.has(peerId)) continue;
      mergedByPeerId.set(peerId, item);
    }

    const myName =
      resolveProfileDisplayName(String(myProfileQuery.data?.display_name ?? ""), String(myProfileQuery.data?.handle ?? "")) ||
      me.displayName ||
      "Sen";
    const myBirthDate = parseBirthDateFromRow(myProfileQuery.data?.birth_date ?? null) ?? me.birthDate;
    const myNumerology = computeKarmaNumerology(myName, myBirthDate);

    const includePeerIfMissing = (peerId: string, providedPeer?: KarmaUser | null) => {
      const id = String(peerId ?? "").trim();
      if (!id || blockedUserIds.has(id) || passedUserIds.has(id) || mergedByPeerId.has(id)) return;

      const peer = providedPeer ?? getUserById(id, store) ?? null;
      if (!peer) return;

      const peerNumerology = computeKarmaNumerology(peer.displayName, peer.birthDate);
      const overview = personalizeMatchOverview(computeMatchOverview(myNumerology, peerNumerology), {
        meName: myName,
        otherName: peer.displayName,
      });

      mergedByPeerId.set(id, { user: peer, overview });
    };

    for (const peerId of sohbetSummaryQuery.data?.keys?.() ?? []) {
      const id = String(peerId);
      includePeerIfMissing(id, summaryPeerById.get(id) ?? null);
    }

    if (selectedSohbetOtherUser?.id) {
      includePeerIfMissing(selectedSohbetOtherUser.id, selectedSohbetOtherUser);
    }

    const base = Array.from(mergedByPeerId.values());
    const q = sohbetQuery.trim().toLocaleLowerCase("tr-TR");
    if (!q) return base;

    return base.filter((item: any) => {
      const haystack = `${item.user.displayName} ${item.user.bio ?? ""} ${(item.overview.notes?.[0] ?? "")}`.toLocaleLowerCase("tr-TR");
      return haystack.includes(q);
    });
  }, [blockedUserIds, passedUserIds, me.birthDate, me.displayName, myProfileQuery.data?.birth_date, myProfileQuery.data?.display_name, myProfileQuery.data?.handle, selectedSohbetOtherUser, sohbetList, sohbetQuery, sohbetSummaryQuery.data, summaryPeerById, store]);

  const begenilerMatches = useMemo(() => {
    const mergedByPeerId = new Map<string, any>();

    for (const item of mutualLikes) {
      const peerId = String(item?.user?.id ?? "").trim();
      if (!peerId || mergedByPeerId.has(peerId)) continue;
      mergedByPeerId.set(peerId, item);
    }

    for (const item of filteredSohbetList) {
      const peerId = String(item?.user?.id ?? "").trim();
      if (!peerId || mergedByPeerId.has(peerId)) continue;
      mergedByPeerId.set(peerId, item);
    }

    return Array.from(mergedByPeerId.values()).sort(
      (a: any, b: any) => Number(b?.overview?.scores?.dailyHarmony ?? 0) - Number(a?.overview?.scores?.dailyHarmony ?? 0),
    );
  }, [filteredSohbetList, mutualLikes]);

  const visibleNewMatches = showAllNewMatches ? filteredSohbetList : filteredSohbetList.slice(0, 8);
  const activeChatCount = filteredSohbetList.length;

  const ensureChatSlotForPeer = useCallback(
    (peerId: string) => {
      if (!isChatLimitedByPlan) return true;
      if (!peerId) return false;
      if (sohbetUserIds.has(peerId)) return true;
      if (activeChatCount < CHAT_ACTIVE_LIMIT_FREE) return true;

      toast({
        title: "Sohbet limiti doldu",
        description: `Free planda aynı anda en fazla ${CHAT_ACTIVE_LIMIT_FREE} aktif sohbet açabilirsin. Plus/Pro ile sınırsız sohbete geçebilirsin.`,
      });
      return false;
    },
    [activeChatCount, isChatLimitedByPlan, sohbetUserIds, toast],
  );
  const recentChats = useMemo(() => {
    return filteredSohbetList
      .map((item: any) => {
        const peerId = String(item?.user?.id ?? "");
        const match =
          peerId
            ? store.matches.find((m: any) => {
                const isWithRealtimeMe = (m.aUserId === realtimeMeId && m.bUserId === peerId) || (m.bUserId === realtimeMeId && m.aUserId === peerId);
                const isWithLocalMe = (m.aUserId === me.id && m.bUserId === peerId) || (m.bUserId === me.id && m.aUserId === peerId);
                return isWithRealtimeMe || isWithLocalMe;
              })
            : null;

        const dbSummary = peerId ? sohbetSummaryQuery.data?.get(peerId) : undefined;
        const dbTimestamp = dbSummary?.createdAt ? new Date(dbSummary.createdAt).getTime() : 0;

        const localTs = match?.lastMessageAt ? new Date(match.lastMessageAt).getTime() : 0;
        const matchMessages = match?.id ? listMessages(match.id, store) : [];
        const lastLocalMessage = matchMessages.length ? matchMessages[matchMessages.length - 1] : null;
        const hasAnyMessage = Boolean(dbSummary?.id) || Boolean(lastLocalMessage?.id);
        if (!hasAnyMessage) return null;

        const candidateTs = Math.max(Number.isFinite(dbTimestamp) ? dbTimestamp : 0, Number.isFinite(localTs) ? localTs : 0);
        const lastMessageText = String(dbSummary?.text ?? lastLocalMessage?.text ?? "").trim();
        const lastMessageTime = String(dbSummary?.createdAt ?? lastLocalMessage?.createdAt ?? match?.lastMessageAt ?? "").trim();

        return {
          ...item,
          _lastTs: candidateTs,
          _previewText: lastMessageText || buildSohbetPreview(item),
          _timeText: lastMessageTime ? formatSohbetTime(lastMessageTime) : "",
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => (b._lastTs ?? 0) - (a._lastTs ?? 0));
  }, [filteredSohbetList, me.id, realtimeMeId, sohbetSummaryQuery.data, store]);
  const sohbetDailyHarmony =
    filteredSohbetList.length > 0
      ? Math.round(filteredSohbetList.reduce((acc: number, item: any) => acc + (item.overview.scores.dailyHarmony ?? 0), 0) / filteredSohbetList.length)
      : 0;
  const sohbetUnreadMap = useMemo(() => getSohbetUnreadMap(), [sohbetUnreadTotal, storeVersion]);
  const firstUnreadChat = useMemo(
    () =>
      recentChats.find((item: any) => {
        const keys = getConversationKeysForPeer({
          peerId: item.user.id,
          realtimeMeId,
          localMeId: me.id,
          matches: store.matches,
        });
        return keys.some((key) => (sohbetUnreadMap[key] ?? 0) > 0);
      }) ?? null,
    [me.id, recentChats, realtimeMeId, sohbetUnreadMap, store.matches],
  );

  useEffect(() => {
    if (!realtimeMeId && !me.id) return;

    const peersMap = new Map<string, KarmaUser>();
    sohbetList.forEach((entry: any) => {
      const peer = entry.user as KarmaUser;
      if (peer?.id) peersMap.set(peer.id, peer);
    });

    if (selectedSohbetOtherUser?.id) {
      peersMap.set(selectedSohbetOtherUser.id, selectedSohbetOtherUser);
    }

    const peers = Array.from(peersMap.values());
    if (!peers.length) return;

    const keyMetaMap = new Map<string, { peerId: string; peer: KarmaUser }>();
    peers.forEach((peer) => {
      const keys = getConversationKeysForPeer({
        peerId: peer.id,
        realtimeMeId,
        localMeId: me.id,
        matches: store.matches,
      });
      keys.forEach((key) => {
        if (!keyMetaMap.has(key)) {
          keyMetaMap.set(key, { peerId: peer.id, peer });
        }
      });
    });

    if (!keyMetaMap.size) return;

    const selfIds = new Set([realtimeMeId, me.id].filter(Boolean));
    const supabase = getSupabaseClient();
    const nextChannels: Record<string, any> = {};

    const channels = Array.from(keyMetaMap.entries()).map(([conversationKey, meta]) => {
      const channel = supabase.channel(`karma-sohbet-${conversationKey}`, {
        config: { broadcast: { self: false } },
      });

      channel.on("broadcast", { event: "message" }, ({ payload }: any) => {
        const incoming = payload as any;
        if (!incoming) return;

        const incomingFromId = typeof incoming.fromUserId === "string" ? incoming.fromUserId.trim() : "";
        const incomingToId = typeof incoming.toUserId === "string" ? incoming.toUserId.trim() : "";

        if (incoming.conversationKey && incoming.conversationKey !== conversationKey) return;
        if (incomingFromId && selfIds.has(incomingFromId)) return;
        if (incomingToId && !selfIds.has(incomingToId)) return;

        const inferredPeerId = incomingFromId && !selfIds.has(incomingFromId) ? incomingFromId : meta.peerId;

        const ensured = ensurePrivateMatchForUser({
          peerId: inferredPeerId,
          peerDisplayName: incoming.fromDisplayName || meta.peer.displayName,
          peerHandle: incoming.fromHandle || meta.peer.handle,
          peerPhotoUrl: incoming.fromPhotoUrl || meta.peer.photos[0]?.url || null,
          peerBirthDate: meta.peer.birthDate,
        });

        if (!ensured.matchId) return;

        appendMessageToMatch({
          matchId: ensured.matchId,
          fromUserId: inferredPeerId,
          text: String(incoming.text || "").trim(),
          messageId: incoming.messageId,
          createdAt: incoming.createdAt,
        });
        setStoreVersion((v) => v + 1);
        void queryClient.invalidateQueries({ queryKey: ["karma-direct-thread", user?.id, inferredPeerId] });
        void queryClient.invalidateQueries({ queryKey: ["karma-direct-summary", user?.id] });
        setTypingMap((prev) => (prev[conversationKey] ? { ...prev, [conversationKey]: false } : prev));

        const isActiveConversation = hasSelectedSohbet && selectedSohbetConversationKeys.includes(conversationKey);
        if (isActiveConversation) return;

        const unreadMap = getSohbetUnreadMap();
        unreadMap[conversationKey] = (unreadMap[conversationKey] ?? 0) + 1;
        setSohbetUnreadMap(unreadMap);
        setSohbetUnreadTotal(getSohbetUnreadTotal());

        const senderName = String(incoming.fromDisplayName || meta.peer.displayName || "Yeni mesaj");
        const snippet = String(incoming.text || "").trim();
        const targetHref = `/panel/kesfet/sohbet?match=${encodeURIComponent(ensured.matchId)}&peer=${encodeURIComponent(inferredPeerId)}`;
        toast({
          title: `${senderName} sana mesaj gönderdi`,
          description: snippet || "Sohbete geçmek için aç butonuna dokun.",
          className: "cursor-pointer",
          onClick: () => navigate(targetHref),
          action: (
            <ToastAction altText="Sohbeti aç" onClick={() => navigate(targetHref)}>
              Aç
            </ToastAction>
          ),
        });
      });

      channel.on("broadcast", { event: "typing" }, ({ payload }: any) => {
        const incoming = payload as any;
        if (!incoming) return;

        const incomingFromId = typeof incoming.fromUserId === "string" ? incoming.fromUserId.trim() : "";
        const incomingToId = typeof incoming.toUserId === "string" ? incoming.toUserId.trim() : "";

        if (incoming.conversationKey && incoming.conversationKey !== conversationKey) return;
        if (incomingFromId && selfIds.has(incomingFromId)) return;
        if (incomingToId && !selfIds.has(incomingToId)) return;

        const isTyping = Boolean(incoming.isTyping);
        setTypingMap((prev) => ({ ...prev, [conversationKey]: isTyping }));

        const prevTimer = typingResetTimersRef.current[conversationKey];
        if (prevTimer) clearTimeout(prevTimer);

        if (isTyping) {
          typingResetTimersRef.current[conversationKey] = setTimeout(() => {
            setTypingMap((prev) => (prev[conversationKey] ? { ...prev, [conversationKey]: false } : prev));
            delete typingResetTimersRef.current[conversationKey];
          }, 1800);
        }
      });

      sohbetRealtimeReadyRef.current[conversationKey] = false;
      channel.subscribe((status) => {
        const normalized = String(status ?? "").toUpperCase();
        sohbetRealtimeReadyRef.current[conversationKey] = normalized === "SUBSCRIBED";
      });
      nextChannels[conversationKey] = channel;
      return channel;
    });

    sohbetRealtimeRef.current = nextChannels;

    return () => {
      channels.forEach((channel) => {
        void supabase.removeChannel(channel);
      });
      Object.values(typingResetTimersRef.current).forEach((timer) => clearTimeout(timer));
      typingResetTimersRef.current = {};
      sohbetRealtimeRef.current = {};
      sohbetRealtimeReadyRef.current = {};
    };
  }, [hasSelectedSohbet, me.id, navigate, queryClient, realtimeMeId, selectedSohbetConversationKeys, selectedSohbetOtherUser, sohbetList, store.matches, toast, user?.id]);

  const onConnectFromLikes = (entry: { user: KarmaUser }) => {
    const targetUserId = String(entry.user.id ?? "").trim();
    if (!targetUserId) return;

    if (canChatEveryone || sohbetUserIds.has(targetUserId) || canDirectMessageWithoutMutual) {
      openPrivateChat(entry);
      return;
    }

    if (!likesGivenUserIds.has(targetUserId) && !likeMutation.isPending) {
      likeMutation.mutate(targetUserId);
      toast({
        title: "Bağ isteği gönderildi",
        description: "Karşılıklı beğeni olunca sohbet açılır.",
      });
      return;
    }

    toast({
      title: "Sohbet henüz açılamıyor",
      description: `Karşı taraf da beğendiğinde mesajlaşma açılır. Pro planda karşılıklı beğeni olmadan günlük ${DIRECT_MESSAGE_DAILY_LIMIT_WITHOUT_MUTUAL} kişiye ilk mesaj gönderebilirsin.`,
    });
  };

  const openCompatibilityReport = (entry: { user: KarmaUser }) => {
    navigate(`/panel/kesfet/uyum/${encodeURIComponent(entry.user.id)}`);
  };

  const openProfileFromLikes = (entry: { user: KarmaUser }) => {
    navigate(`/panel/kesfet/profil/${encodeURIComponent(entry.user.id)}`);
  };

  const connectFromCompatibility = () => {
    if (!compatibilityDetail || likeMutation.isPending) return;
    const targetId = compatibilityDetail.targetUser.id;
    const previousLiked = isCompatibilityLiked;
    const nextLiked = !previousLiked;

    setCompatibilityLikeOverride((prev) => ({
      ...prev,
      [targetId]: nextLiked,
    }));

    likeMutation.mutate(targetId, {
      onError: () => {
        setCompatibilityLikeOverride((prev) => ({
          ...prev,
          [targetId]: previousLiked,
        }));
      },
    });
  };

  const messageFromCompatibility = () => {
    if (!compatibilityDetail) return;
    const entry = { user: compatibilityDetail.targetUser };

    if (canChatEveryone || sohbetUserIds.has(entry.user.id) || canDirectMessageWithoutMutual) {
      if (!ensureChatSlotForPeer(entry.user.id)) return;
      openPrivateChat(entry);
      return;
    }

    toast({
      title: "Mesaj henüz açılamıyor",
      description: `Önce Bağ Kur ile istek gönder. Karşılıklı beğeni sonrası sohbet açılır. Pro planda günlük ${DIRECT_MESSAGE_DAILY_LIMIT_WITHOUT_MUTUAL} kişiye doğrudan ilk mesaj hakkı bulunur.`,
    });
  };

  const shareCompatibility = async () => {
    if (!compatibilityDetail) return;

    const shareUrl = new URL(`/panel/kesfet/uyum/${encodeURIComponent(compatibilityDetail.targetUser.id)}`, window.location.origin).toString();

    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: "Uyum Raporu",
          text: `${compatibilityDetail.targetUser.displayName} ile uyum raporu`,
          url: shareUrl,
        });
        return;
      }

      let copied = false;

      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          copied = true;
        } catch {
          copied = false;
        }
      }

      if (!copied && typeof document !== "undefined") {
        const area = document.createElement("textarea");
        area.value = shareUrl;
        area.setAttribute("readonly", "true");
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        copied = document.execCommand("copy");
        document.body.removeChild(area);
      }

      if (copied) {
        toast({
          title: "Link kopyalandı",
          description: "Uyum raporu bağlantısını istediğin kişiye gönderebilirsin.",
        });
        return;
      }

      toast({
        title: "Paylaşım hazır",
        description: "Tarayıcı otomatik paylaşımı desteklemedi. Linki adres çubuğundan kopyalayarak paylaşabilirsin.",
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      toast({
        title: "Paylaşım iptal edildi",
        description: "Paylaşım ekranı kapatıldı.",
      });
    }
  };

  const openPrivateChat = (entry: { user: KarmaUser }) => {
    const peerId = String(entry?.user?.id ?? "").trim();
    if (!ensureChatSlotForPeer(peerId)) return;
    if (peerId && blockedUserIds.has(peerId)) {
      toast({
        title: "Kullanıcı engelli",
        description: "Sohbet açmak için önce engeli kaldırmalısın.",
        variant: "destructive",
      });
      return;
    }

    if (peerId && passedUserIds.has(peerId)) {
      toast({
        title: "Bu kişi geçildi",
        description: "Geç dediğin kişiler sohbet listesinde görünmez.",
      });
      return;
    }

    const ensured = ensurePrivateMatchForUser({
      peerId: entry.user.id,
      peerDisplayName: entry.user.displayName,
      peerHandle: entry.user.handle,
      peerPhotoUrl: entry.user.photos[0]?.url ?? null,
      peerBirthDate: entry.user.birthDate,
    });

    if (!ensured?.matchId) {
      toast({
        title: "Sohbet açılamadı",
        description: "Bu kullanıcı için özel sohbet başlatılamadı.",
        variant: "destructive",
      });
      return;
    }

    const unreadMap = getSohbetUnreadMap();
    const conversationKeys = getConversationKeysForPeer({
      peerId: entry.user.id,
      realtimeMeId,
      localMeId: me.id,
      matches: store.matches,
    });
    let changed = false;
    conversationKeys.forEach((key) => {
      if (!unreadMap[key]) return;
      unreadMap[key] = 0;
      changed = true;
    });
    if (changed) {
      setSohbetUnreadMap(unreadMap);
      setSohbetUnreadTotal(getSohbetUnreadTotal());
    }

    setActiveRecentChatMenuPeerId("");
    setStoreVersion((v) => v + 1);
    navigate(`/panel/kesfet/sohbet?match=${encodeURIComponent(ensured.matchId)}&peer=${encodeURIComponent(entry.user.id)}`);
  };

  const closeSohbetDetail = () => {
    navigate("/panel/kesfet/sohbet");
  };

  useEffect(() => {
    if (!hasSelectedSohbet) return;

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };

    const preventPinchZoom = (event: TouchEvent) => {
      if ((event as any).scale && (event as any).scale !== 1) {
        event.preventDefault();
      }
    };

    document.addEventListener("gesturestart", preventGesture, { passive: false });
    document.addEventListener("gesturechange", preventGesture, { passive: false });
    document.addEventListener("gestureend", preventGesture, { passive: false });
    document.addEventListener("touchmove", preventPinchZoom, { passive: false });

    return () => {
      document.removeEventListener("gesturestart", preventGesture as EventListener);
      document.removeEventListener("gesturechange", preventGesture as EventListener);
      document.removeEventListener("gestureend", preventGesture as EventListener);
      document.removeEventListener("touchmove", preventPinchZoom as EventListener);
    };
  }, [hasSelectedSohbet]);

  const openUnreadFromBell = () => {
    if (firstUnreadChat) {
      openPrivateChat(firstUnreadChat);
      return;
    }

    toast({
      title: "Yeni bildirim yok",
      description: "Okunmamış mesaj bulunmuyor.",
    });
  };

  const emitTypingState = async (isTyping: boolean) => {
    if (!selectedSohbetOtherUser || !selectedSohbetConversationKeys.length) return;

    const readyTargets = selectedSohbetConversationKeys
      .map((conversationKey) => {
        const channel = sohbetRealtimeRef.current[conversationKey];
        if (!channel) return null;
        return { conversationKey, channel };
      })
      .filter(Boolean) as Array<{ conversationKey: string; channel: any }>;

    if (!readyTargets.length) {
      const fallbackReady = await waitForReadySohbetChannel(selectedSohbetConversationKeys, 800);
      if (!fallbackReady) return;
      readyTargets.push(fallbackReady);
    }

    await Promise.allSettled(
      readyTargets.map((target) =>
        target.channel.send({
          type: "broadcast",
          event: "typing",
          payload: {
            conversationKey: target.conversationKey,
            fromUserId: selectedSohbetSenderId,
            toUserId: selectedSohbetTargetId || selectedSohbetOtherUser.id,
            isTyping,
          },
        }),
      ),
    );
  };

  const sendSohbetMessage = async () => {
    if (!selectedSohbetMatch || !selectedSohbetOtherUser || !selectedSohbetConversationKeys.length) return;
    const trimmed = sohbetMessage.trim();
    if (!trimmed) return;

    if (typingStopBroadcastRef.current) {
      clearTimeout(typingStopBroadcastRef.current);
      typingStopBroadcastRef.current = null;
    }
    if (isTypingBroadcastedRef.current) {
      isTypingBroadcastedRef.current = false;
      void emitTypingState(false);
    }

    const targetUserId = selectedSohbetTargetId || selectedSohbetOtherUser.id;
    if (!targetUserId) return;

    let createdMessage: { id: string; fromUserId: string; toUserId: string; text: string; createdAt: string } | null = null;

    try {
      createdMessage = await sendDirectMessageToUser({
        toUserId: targetUserId,
        body: trimmed,
      });

      appendMessageToMatch({
        matchId: selectedSohbetMatch.id,
        fromUserId: createdMessage.fromUserId,
        text: createdMessage.text,
        messageId: createdMessage.id,
        createdAt: createdMessage.createdAt,
      });

      setSohbetMessage("");
      setStoreVersion((v) => v + 1);
      void queryClient.invalidateQueries({ queryKey: ["karma-direct-thread", user?.id, targetUserId] });
      void queryClient.invalidateQueries({ queryKey: ["karma-direct-summary", user?.id] });
    } catch (error) {
      toast({
        title: "Mesaj gönderilemedi",
        description: error instanceof Error ? error.message : "Mesaj veritabanına kaydedilemedi.",
        variant: "destructive",
      });
      return;
    }

    if (!createdMessage) return;

    const readyTargets = selectedSohbetConversationKeys
      .map((conversationKey) => {
        const channel = sohbetRealtimeRef.current[conversationKey];
        if (!channel) return null;
        return { conversationKey, channel };
      })
      .filter(Boolean) as Array<{ conversationKey: string; channel: any }>;

    if (!readyTargets.length) {
      const fallbackReady = await waitForReadySohbetChannel(selectedSohbetConversationKeys, 600);
      if (!fallbackReady) return;
      readyTargets.push(fallbackReady);
    }

    await Promise.allSettled(
      readyTargets.map((target) =>
        target.channel.send({
          type: "broadcast",
          event: "message",
          payload: {
            conversationKey: target.conversationKey,
            messageId: createdMessage.id,
            fromUserId: createdMessage.fromUserId,
            toUserId: createdMessage.toUserId,
            fromDisplayName: senderDisplayName,
            fromHandle: senderHandle,
            fromPhotoUrl: senderPhotoUrl,
            text: createdMessage.text,
            createdAt: createdMessage.createdAt,
          },
        }),
      ),
    );
  };

  const deleteSohbetMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      await deleteDirectMessageById(messageId);
      deleteMessageById(messageId);
    },
    onSuccess: () => {
      setStoreVersion((v) => v + 1);
      if (selectedSohbetPeerId) {
        void queryClient.invalidateQueries({ queryKey: ["karma-direct-thread", user?.id, selectedSohbetPeerId] });
      }
      void queryClient.invalidateQueries({ queryKey: ["karma-direct-summary", user?.id] });
      toast({ title: "Mesaj geri alındı" });
    },
    onError: (error) => {
      toast({
        title: "Mesaj geri alınamadı",
        description: error instanceof Error ? error.message : "Mesaj geri alınırken bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  const editSohbetMessageMutation = useMutation({
    mutationFn: async (args: { messageId: string; text: string }) => {
      return await updateDirectMessageById({ messageId: args.messageId, body: args.text });
    },
    onSuccess: async () => {
      if (!selectedSohbetPeerId) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["karma-direct-thread", user?.id, selectedSohbetPeerId] }),
        queryClient.invalidateQueries({ queryKey: ["karma-direct-summary", user?.id] }),
      ]);
      setEditingMessageId("");
      setEditingMessageText("");
      toast({ title: "Mesaj güncellendi" });
    },
    onError: (error) => {
      toast({
        title: "Mesaj güncellenemedi",
        description: error instanceof Error ? error.message : "Mesaj düzenlenirken bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  const deleteConversationMutation = useMutation({
    mutationFn: async (peerUserId: string) => {
      await deleteDirectConversationWithUser(peerUserId);
    },
    onSuccess: async (_res, peerUserId) => {
      clearLocalConversationForPeer(peerUserId);
      setHiddenMessageIds({});
      setStoreVersion((v) => v + 1);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["karma-direct-thread", user?.id, peerUserId] }),
        queryClient.invalidateQueries({ queryKey: ["karma-direct-summary", user?.id] }),
      ]);
      if (selectedSohbetPeerId === peerUserId) {
        navigate("/panel/kesfet/sohbet");
      }
      toast({ title: "Yazışma silindi" });
    },
    onError: (error) => {
      toast({
        title: "Yazışma silinemedi",
        description: error instanceof Error ? error.message : "Yazışma silinirken bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  const blockConversationUserMutation = useMutation({
    mutationFn: async (peerUserId: string) => {
      return await blockUserAndDeleteConversation(peerUserId);
    },
    onSuccess: async (result, peerUserId) => {
      if (result.blocked) {
        clearLocalConversationForPeer(peerUserId);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["karma-direct-thread", user?.id, peerUserId] }),
        queryClient.invalidateQueries({ queryKey: ["karma-direct-summary", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["karma-interactions", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["karma-likes-and-matches", user?.id] }),
      ]);
      if (selectedSohbetPeerId === peerUserId) {
        navigate("/panel/kesfet/sohbet");
      }
      toast({
        title: result.blocked ? "Kullanıcı engellendi" : "Kullanıcı engeli kaldırıldı",
        description: result.blocked ? "Yazışma geçmişi temizlendi." : undefined,
      });
    },
    onError: (error) => {
      toast({
        title: "Engelleme işlemi başarısız",
        description: error instanceof Error ? error.message : "İşlem sırasında bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (!activeRecentChatMenuPeerId) return;
    if (recentChats.some((item: any) => item.user.id === activeRecentChatMenuPeerId)) return;
    setActiveRecentChatMenuPeerId("");
  }, [activeRecentChatMenuPeerId, recentChats]);

  useEffect(() => {
    if (activeTab === "begeniler") return;
    setShowLikesSettings(false);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "kesfet") return;
    setShowDiscoveryFilters(false);
  }, [activeTab]);

  useEffect(() => {
    const profile = myProfileQuery.data;
    if (!profile) return;

    setDiscoveryMinAge(18);
    setDiscoveryMaxAge(99);
    setDiscoveryGender("herkes");
    setDiscoveryDestinyNumber(0);
    setDiscoveryHeartNumber(0);
    setDiscoveryExpressionNumber(0);
  }, [myProfileQuery.data?.id]);

  useEffect(() => {
    if (hasAdvancedFilters) return;
    setShowLikesSettings(false);
  }, [hasAdvancedFilters]);

  useEffect(() => {
    writeLikesFilterPrefs({
      minHarmony: likesMinHarmony,
      sortBy: likesSortBy,
      onlyWithPhoto: likesOnlyWithPhoto,
    });
  }, [likesMinHarmony, likesOnlyWithPhoto, likesSortBy]);

  useEffect(() => {
    if (!showLikesSettings) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (likesSettingsRef.current?.contains(target)) return;
      setShowLikesSettings(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [showLikesSettings]);

  useEffect(() => {
    if (!showDiscoveryFilters) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (discoveryFiltersRef.current?.contains(target)) return;
      setShowDiscoveryFilters(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [showDiscoveryFilters]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickedInsideMenu = target.closest("[data-chat-menu-root='message'], [data-chat-menu-root='header'], [data-chat-menu-root='recent'], [data-chat-menu-root='emoji']");
      if (clickedInsideMenu) return;

      setActiveMessageMenuId("");
      setShowSohbetHeaderMenu(false);
      setActiveRecentChatMenuPeerId("");
      setShowEmojiPicker(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  const handleSelectEmoji = useCallback((emoji: string) => {
    const nextEmoji = String(emoji ?? "").trim();
    if (!nextEmoji) return;

    setSohbetMessage((prev) => `${prev}${nextEmoji}`);
    const burstId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const randomX = Math.floor(Math.random() * 72) - 16;
    setEmojiBursts((prev) => [...prev, { id: burstId, emoji: nextEmoji, x: randomX }]);

    setTimeout(() => {
      setEmojiBursts((prev) => prev.filter((entry) => entry.id !== burstId));
    }, 900);
  }, []);

  const handleEditSohbetMessage = (message: { id: string; text: string }) => {
    const messageId = String(message.id ?? "").trim();
    if (!messageId) return;
    setEditingMessageId(messageId);
    setEditingMessageText(String(message.text ?? ""));
    setActiveMessageMenuId("");
  };

  const handleLocalHideSohbetMessage = (messageId: string) => {
    const id = String(messageId ?? "").trim();
    if (!id) return;
    setHiddenMessageIds((prev) => ({ ...prev, [id]: true }));
    setActiveMessageMenuId("");
    toast({ title: "Mesaj gizlendi", description: "Sadece senin ekranından kaldırıldı." });
  };

  const handleRetractSohbetMessage = (messageId: string) => {
    const id = String(messageId ?? "").trim();
    if (!id) return;
    setActiveMessageMenuId("");
    deleteSohbetMessageMutation.mutate(id);
  };

  const clearLocalConversationForPeer = useCallback((peerUserId: string) => {
    const peerId = String(peerUserId ?? "").trim();
    if (!peerId) return;

    const match = store.matches.find((m: any) => {
      const isWithRealtimeMe = (m.aUserId === realtimeMeId && m.bUserId === peerId) || (m.bUserId === realtimeMeId && m.aUserId === peerId);
      const isWithLocalMe = (m.aUserId === me.id && m.bUserId === peerId) || (m.bUserId === me.id && m.aUserId === peerId);
      return isWithRealtimeMe || isWithLocalMe;
    });

    if (!match?.id) return;

    const messages = listMessages(match.id, store);
    if (!messages.length) return;

    messages.forEach((message) => {
      deleteMessageById(message.id);
    });

    setHiddenMessageIds((prev) => {
      const next = { ...prev };
      messages.forEach((message) => {
        delete next[String(message.id ?? "")];
      });
      return next;
    });
  }, [me.id, realtimeMeId, store]);

  const handleDeleteConversation = (peerUserId: string) => {
    const id = String(peerUserId ?? "").trim();
    if (!id) return;
    setShowSohbetHeaderMenu(false);
    setActiveRecentChatMenuPeerId("");
    deleteConversationMutation.mutate(id);
  };

  const handleBlockConversationUser = (peerUserId: string) => {
    const id = String(peerUserId ?? "").trim();
    if (!id) return;
    setShowSohbetHeaderMenu(false);
    setActiveRecentChatMenuPeerId("");
    blockConversationUserMutation.mutate(id);
  };

  const likeMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!user?.id) throw new Error("Oturum yok");
      const supabase = getSupabaseClient();

      const { data: existing, error: existingError } = await supabase
        .from("karma_likes")
        .select("id")
        .eq("from_user_id", user.id)
        .eq("to_user_id", targetUserId)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existing?.id) {
        const { error: delError } = await supabase.from("karma_likes").delete().eq("id", existing.id);
        if (delError) throw delError;
        return { added: false, mutual: false };
      }

      const { error: insError } = await supabase
        .from("karma_likes")
        .insert({ from_user_id: user.id, to_user_id: targetUserId });
      if (insError) throw insError;

      const { data: reverse, error: reverseError } = await supabase
        .from("karma_likes")
        .select("id")
        .eq("from_user_id", targetUserId)
        .eq("to_user_id", user.id)
        .maybeSingle();
      if (reverseError) throw reverseError;

      return { added: true, mutual: !!reverse?.id };
    },
    onSuccess: (result) => {
      if (result.added && result.mutual) {
        toast({
          title: "Yeni eşleşme!",
          description: "Karşılıklı beğeni oldu. Keşfet > Sohbet alanından özel sohbeti açabilirsin.",
        });
      }

      void queryClient.invalidateQueries({ queryKey: ["karma-interactions", user?.id] });
      void queryClient.invalidateQueries({ queryKey: ["karma-candidates", user?.id] });
      void queryClient.invalidateQueries({ queryKey: ["karma-likes-and-matches", user?.id] });
    },
  });

  const passMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!user?.id) throw new Error("Oturum yok");
      const supabase = getSupabaseClient();

      const { error } = await supabase.from("karma_passes").upsert(
        {
          from_user_id: user.id,
          to_user_id: targetUserId,
        },
        { onConflict: "from_user_id,to_user_id" },
      );

      if (error) throw error;
      return { ok: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["karma-interactions", user?.id] });
      void queryClient.invalidateQueries({ queryKey: ["karma-candidates", user?.id] });
      void queryClient.invalidateQueries({ queryKey: ["karma-likes-and-matches", user?.id] });
    },
  });

  const advanceDiscoveryCard = useCallback((targetUserId: string) => {
    setDismissedIds((prev) => (prev.includes(targetUserId) ? prev : [...prev, targetUserId]));
    setCursor((v) => v + 1);
  }, []);

  const requestLikeFromDiscovery = useCallback(
    (targetUserId: string) => {
      const cleanTargetId = String(targetUserId ?? "").trim();
      if (!cleanTargetId || likeMutation.isPending) return;
      if (!ensureDiscoveryQuota()) return;

      advanceDiscoveryCard(cleanTargetId);

      if (likesGivenUserIds.has(cleanTargetId)) return;

      likeMutation.mutate(cleanTargetId, {
        onError: () => {
          toast({
            title: "Bağ isteği gönderilemedi",
            description: "Bağlantı sorunu nedeniyle işlem tamamlanamadı.",
          });
        },
      });
    },
    [advanceDiscoveryCard, ensureDiscoveryQuota, likeMutation, likesGivenUserIds, toast],
  );

  const requestPassFromDiscovery = useCallback(
    (targetUserId: string) => {
      const cleanTargetId = String(targetUserId ?? "").trim();
      if (!cleanTargetId || passMutation.isPending) return;
      if (!ensureDiscoveryQuota()) return;

      advanceDiscoveryCard(cleanTargetId);

      passMutation.mutate(cleanTargetId, {
        onError: () => {
          toast({
            title: "Geç işlemi tamamlanamadı",
            description: "Bağlantı sorunu nedeniyle işlem tamamlanamadı.",
          });
        },
      });
    },
    [advanceDiscoveryCard, ensureDiscoveryQuota, passMutation, toast],
  );

  const loading =
    activeTab === "sohbet"
      ? (hasSelectedSohbet ? selectedSohbetDbThreadQuery.isLoading : false)
      : myProfileQuery.isLoading ||
        interactionQuery.isLoading ||
        (activeTab === "kesfet"
          ? candidatesQuery.isLoading
          : activeTab === "uyum" || activeTab === "profil"
            ? compatibilityTargetQuery.isLoading
            : likesAndMatchesQuery.isLoading);
  const error =
    (myProfileQuery.isError && myProfileQuery.error) ||
    (activeTab === "sohbet"
      ? hasSelectedSohbet
        ? selectedSohbetDbThreadQuery.isError && selectedSohbetDbThreadQuery.error
        : null
      : (interactionQuery.isError && interactionQuery.error) ||
        (activeTab === "kesfet"
          ? candidatesQuery.isError && candidatesQuery.error
          : activeTab === "uyum" || activeTab === "profil"
            ? compatibilityTargetQuery.isError && compatibilityTargetQuery.error
            : likesAndMatchesQuery.isError && likesAndMatchesQuery.error));
  const hasSoftNetworkError = Boolean(error) && isFailedFetchLikeError(error);

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-[1400px] min-h-0 sm:pb-0",
        activeTab === "sohbet" && hasSelectedSohbet ? "h-full overflow-hidden pb-0" : "pb-24",
      )}
    >
      <div
        className={cn(
          "grid grid-cols-1",
          activeTab === "sohbet" && hasSelectedSohbet ? "h-full min-h-0" : "gap-5 sm:gap-8",
        )}
      >
        <section
          className={cn(
            "min-w-0",
            activeTab === "sohbet" && hasSelectedSohbet ? "flex h-full min-h-0 flex-col" : "space-y-5",
          )}
        >
          {activeTab === "begeniler" ? (
            <div className="relative z-30 space-y-4 rounded-[1.75rem] bg-slate-50/85 p-5 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12),0_10px_25px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:bg-slate-900/35">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Heart className="h-5 w-5 text-[var(--panel-primary)]" />
                  <div className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">Beğeniler</div>
                </div>
                <div ref={likesSettingsRef} className="relative flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!hasAdvancedFilters) {
                        toast({
                          title: "Gelişmiş filtreler Plus/Pro ile açılır",
                          description: "Minimum uyum, sıralama ve fotoğraflı profil filtreleri premium özelliktir.",
                        });
                        return;
                      }
                      setShowLikesSettings((prev) => !prev);
                    }}
                    className={cn(
                      "grid size-9 place-items-center rounded-full bg-[var(--panel-primary)]/20 text-[var(--panel-primary)] transition",
                      !hasAdvancedFilters ? "opacity-65" : "",
                      showLikesSettings ? "ring-2 ring-[var(--panel-primary)]/35" : "",
                    )}
                    aria-label="Beğeni ayarlarını aç"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                  </button>

                  {showLikesSettings ? (
                    <div className="absolute right-0 top-11 z-[90] w-[280px] rounded-2xl border border-[var(--panel-primary)]/25 bg-white p-3 shadow-2xl dark:bg-[#17112a]">
                      <div className="text-xs font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">Beğeni Ayarları</div>

                      <div className="mt-3 space-y-3">
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
                          Minimum Uyum: %{likesMinHarmony}
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={5}
                            value={likesMinHarmony}
                            onChange={(event) => setLikesMinHarmony(Number(event.target.value) || 0)}
                            className="mt-1 w-full accent-[var(--panel-primary)]"
                          />
                        </label>

                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
                          Sıralama
                          <select
                            value={likesSortBy}
                            onChange={(event) => setLikesSortBy(event.target.value as LikesSortBy)}
                            className="mt-1 h-9 w-full rounded-xl border border-[var(--panel-primary)]/20 bg-white px-3 text-sm text-slate-900 outline-none dark:bg-[#231a38] dark:text-slate-100"
                          >
                            <option value="newest">En Yeni</option>
                            <option value="harmony">En Yüksek Uyum</option>
                            <option value="name">İsme Göre</option>
                          </select>
                        </label>

                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200">
                          <input
                            type="checkbox"
                            checked={likesOnlyWithPhoto}
                            onChange={(event) => setLikesOnlyWithPhoto(event.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 accent-[var(--panel-primary)]"
                          />
                          Sadece fotoğraflı profiller
                        </label>

                        <button
                          type="button"
                          onClick={() => {
                            setLikesMinHarmony(0);
                            setLikesSortBy("newest");
                            setLikesOnlyWithPhoto(false);
                          }}
                          className="w-full rounded-xl border border-[var(--panel-primary)]/20 px-3 py-2 text-xs font-bold text-[var(--panel-primary)] transition hover:bg-[var(--panel-primary)]/10"
                        >
                          Filtreleri Sıfırla
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="size-9 overflow-hidden rounded-full border-2 border-[var(--panel-primary)]/30">
                    <img
                      src={myProfileQuery.data?.photo_url || "/placeholder.svg"}
                      alt="Profil"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-xl bg-[#efe6f6] p-1.5 dark:bg-[#1f1735]">
                <button
                  type="button"
                  onClick={() => setActiveBegenilerTab("liked")}
                  className={cn(
                    "flex-1 rounded-lg px-3 py-2 text-xs font-bold transition",
                    activeBegenilerTab === "liked"
                      ? "bg-white text-[var(--panel-primary)] shadow-sm"
                      : "text-slate-500 hover:bg-white/70 dark:text-slate-300 dark:hover:bg-white/10",
                  )}
                >
                  Beni Beğenenler
                </button>
                <button
                  type="button"
                  onClick={() => setActiveBegenilerTab("matches")}
                  className={cn(
                    "flex-1 rounded-lg px-3 py-2 text-xs font-bold transition",
                    activeBegenilerTab === "matches"
                      ? "bg-white text-[var(--panel-primary)] shadow-sm"
                      : "text-slate-500 hover:bg-white/70 dark:text-slate-300 dark:hover:bg-white/10",
                  )}
                >
                  Eşleşmeler
                </button>
              </div>

              <div>
                <div className="text-[24px] font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-[30px]">
                  {activeBegenilerTab === "liked" ? `Seni Beğenen ${likesCards.length} Kişi` : "Yeni Eşleşmeler"}
                </div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {activeBegenilerTab === "liked"
                    ? "Kimlerin seninle eşleşmek istediğini keşfet."
                    : "Karşılıklı beğendiğiniz profiller burada açık şekilde listelenir."}
                </div>
              </div>
            </div>
          ) : activeTab === "sohbet" ? (
            hasSelectedSohbet ? null : (
              <div className="space-y-5 rounded-[2rem] border border-[#e7dcec] bg-[#fbf4ff]/95 p-5 shadow-[0_18px_40px_rgba(112,42,225,0.08)]">
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-black tracking-tight text-[#12172b]">Mesajlar</div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={openUnreadFromBell}
                      className="relative grid size-10 place-items-center rounded-full bg-white text-[var(--panel-primary)] shadow-sm transition hover:bg-[var(--panel-primary)]/10"
                      aria-label="Okunmamış mesajı aç"
                    >
                      <Bell className="h-4 w-4" />
                      {sohbetUnreadTotal > 0 ? (
                        <span className="absolute -right-1 -top-1 grid min-h-4 min-w-4 place-items-center rounded-full bg-[var(--panel-primary)] px-1 text-[10px] font-bold leading-none text-white">
                          {Math.min(sohbetUnreadTotal, 99)}
                        </span>
                      ) : null}
                    </button>
                    <button type="button" className="grid size-10 place-items-center rounded-full bg-white text-[var(--panel-primary)] shadow-sm">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={sohbetQuery}
                    onChange={(e) => setSohbetQuery(e.target.value)}
                    placeholder="Kişi veya mesaj ara..."
                    className="h-14 w-full rounded-xl border-none bg-[#ece4f1] pl-12 pr-4 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-500 focus:ring-2 focus:ring-[var(--panel-primary)]/20"
                  />
                </div>
              </div>
            )
          ) : activeTab === "uyum" ? (
            <div className="flex items-center justify-between rounded-[1.75rem] border border-[var(--panel-primary)]/15 bg-[#130d24]/80 p-5">
              <div>
                <div className="text-2xl font-extrabold tracking-tight text-white">Numerolojik Uyum Analizi</div>
                <div className="mt-1 text-sm text-slate-400">Kişisel haritalarınıza göre hazırlanan detaylı uyum raporu</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/panel/kesfet")}
                className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
              >
                Keşfet'e Dön
              </button>
            </div>
          ) : activeTab === "profil" ? (
            <div className="flex items-center justify-between rounded-[1.75rem] bg-slate-50/85 p-5 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12),0_10px_25px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:bg-slate-900/35">
              <div>
                <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">Profil Görünümü</div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Seçtiğin kişinin Keşfet profil kartı</div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/panel/kesfet/begeniler")}
                className="rounded-full border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/8 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-[var(--panel-primary)]/15 dark:text-slate-200"
              >
                Beğeniler'e Dön
              </button>
            </div>
          ) : (
            <div className="relative z-30" ref={discoveryFiltersRef}>
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">Keşfet tercihlerini buradan daraltabilirsin.</div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 rounded-full border-[var(--panel-primary)]/25 bg-white/80 px-3 text-xs font-extrabold text-[var(--panel-primary)] hover:bg-[var(--panel-primary)]/10 dark:bg-white/5"
                  onClick={() => setShowDiscoveryFilters((prev) => !prev)}
                >
                  <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Arama tercihleri
                </Button>
              </div>

              {showDiscoveryFilters ? (
                <div className="mt-3 rounded-2xl border border-[var(--panel-primary)]/20 bg-white/95 p-4 shadow-xl dark:bg-[#17112a]">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Min Yaş
                      <input
                        type="number"
                        min={18}
                        max={99}
                        value={discoveryMinAge}
                        onChange={(event) => {
                          const next = Number(event.target.value);
                          if (!Number.isFinite(next)) return;
                          setDiscoveryMinAge(next);
                        }}
                        onBlur={() => {
                          setDiscoveryMinAge((prev) => Math.max(18, Math.min(99, Math.trunc(prev) || 18)));
                        }}
                        className="mt-1 h-10 w-full rounded-xl border border-[var(--panel-primary)]/20 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[var(--panel-primary)]/20 dark:bg-[#231a38] dark:text-slate-100"
                      />
                    </label>

                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Max Yaş
                      <input
                        type="number"
                        min={18}
                        max={99}
                        value={discoveryMaxAge}
                        onChange={(event) => {
                          const next = Number(event.target.value);
                          if (!Number.isFinite(next)) return;
                          setDiscoveryMaxAge(next);
                        }}
                        onBlur={() => {
                          setDiscoveryMaxAge((prev) => Math.max(18, Math.min(99, Math.trunc(prev) || 99)));
                        }}
                        className="mt-1 h-10 w-full rounded-xl border border-[var(--panel-primary)]/20 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[var(--panel-primary)]/20 dark:bg-[#231a38] dark:text-slate-100"
                      />
                    </label>
                  </div>

                  <label className="mt-3 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                    Cinsiyet
                    <select
                      value={discoveryGender}
                      onChange={(event) => setDiscoveryGender(normalizePreferredGender(event.target.value))}
                      className="mt-1 h-10 w-full rounded-xl border border-[var(--panel-primary)]/20 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[var(--panel-primary)]/20 dark:bg-[#231a38] dark:text-slate-100"
                    >
                      <option value="herkes">Herkes</option>
                      <option value="kadin">Kadın</option>
                      <option value="erkek">Erkek</option>
                      <option value="non_binary">Non-binary</option>
                    </select>
                  </label>

                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Yaşam Yolu
                      <select
                        value={String(discoveryDestinyNumber)}
                        onChange={(event) => setDiscoveryDestinyNumber(Math.max(0, Math.min(9, Number(event.target.value) || 0)))}
                        className="mt-1 h-10 w-full rounded-xl border border-[var(--panel-primary)]/20 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[var(--panel-primary)]/20 dark:bg-[#231a38] dark:text-slate-100"
                      >
                        <option value="0">Hepsi</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                          <option key={`destiny-${digit}`} value={digit}>{digit}</option>
                        ))}
                      </select>
                    </label>

                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Kalp Arzusu
                      <select
                        value={String(discoveryHeartNumber)}
                        onChange={(event) => setDiscoveryHeartNumber(Math.max(0, Math.min(9, Number(event.target.value) || 0)))}
                        className="mt-1 h-10 w-full rounded-xl border border-[var(--panel-primary)]/20 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[var(--panel-primary)]/20 dark:bg-[#231a38] dark:text-slate-100"
                      >
                        <option value="0">Hepsi</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                          <option key={`heart-${digit}`} value={digit}>{digit}</option>
                        ))}
                      </select>
                    </label>

                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      İfade Numarası
                      <select
                        value={String(discoveryExpressionNumber)}
                        onChange={(event) => setDiscoveryExpressionNumber(Math.max(0, Math.min(9, Number(event.target.value) || 0)))}
                        className="mt-1 h-10 w-full rounded-xl border border-[var(--panel-primary)]/20 bg-white px-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-[var(--panel-primary)]/20 dark:bg-[#231a38] dark:text-slate-100"
                      >
                        <option value="0">Hepsi</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                          <option key={`expression-${digit}`} value={digit}>{digit}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setDiscoveryMinAge(18);
                        setDiscoveryMaxAge(99);
                        setDiscoveryGender("herkes");
                        setDiscoveryDestinyNumber(0);
                        setDiscoveryHeartNumber(0);
                        setDiscoveryExpressionNumber(0);
                      }}
                      className="rounded-xl border border-[var(--panel-primary)]/20 px-3 py-2 text-xs font-bold text-[var(--panel-primary)] transition hover:bg-[var(--panel-primary)]/10"
                    >
                      Filtreleri sıfırla
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {loading ? (
            <Card className="rounded-3xl border-slate-200 bg-white p-6 text-sm text-slate-600 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900/40 dark:text-slate-400">
              {activeTab === "kesfet" ? "Keşfet yükleniyor..." : "Liste yükleniyor..."}
            </Card>
          ) : error instanceof Error && !hasSoftNetworkError ? (
            <Card className="rounded-3xl border-red-200 bg-white p-6 text-sm text-red-600 dark:border-red-500/30 dark:bg-slate-900/40">
              {activeTab === "sohbet" ? "Sohbet şu anda yüklenemedi. Lütfen tekrar dene." : error.message}
            </Card>
          ) : activeTab === "kesfet" ? (
            current ? (
              <div className="space-y-4 pb-28">
                <motion.div
                  key={current.user.id}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.15}
                  onDragEnd={(_, info) => {
                    if (info.offset.x > 120) {
                      requestLikeFromDiscovery(current.user.id);
                      return;
                    }
                    if (info.offset.x < -120) {
                      requestPassFromDiscovery(current.user.id);
                      return;
                    }
                  }}
                  className="mx-auto w-full max-w-[640px] touch-pan-y md:max-w-[720px] xl:max-w-[820px]"
                >
                  <div className="overflow-hidden rounded-[2.5rem] shadow-2xl">
                    <DiscoveryCard
                      user={current.user}
                      overview={current.overview}
                      onLike={() => requestLikeFromDiscovery(current.user.id)}
                      onOpenCompatibility={() => openCompatibilityReport(current)}
                      hideDetail
                    />
                  </div>
                </motion.div>

                <div className="fixed inset-x-0 bottom-[calc(5.25rem+env(safe-area-inset-bottom))] z-30 flex justify-center px-4 md:bottom-8">
                  <div className="flex w-full max-w-[360px] items-center justify-center gap-4 rounded-[1.8rem] bg-[#f4edf8]/92 p-2.5 shadow-[0_10px_30px_rgba(15,23,42,0.18)] backdrop-blur-md">
                    <button
                      type="button"
                      disabled={passMutation.isPending}
                      onClick={() => requestPassFromDiscovery(current.user.id)}
                      className="grid size-16 place-items-center rounded-2xl bg-[#121212] text-[#f8df8a] shadow-sm transition hover:brightness-110 disabled:opacity-60"
                      aria-label="Geç"
                    >
                      <X className="h-7 w-7" />
                    </button>

                    <button
                      type="button"
                      disabled={likeMutation.isPending}
                      onClick={() => requestLikeFromDiscovery(current.user.id)}
                      className="grid size-16 place-items-center rounded-2xl bg-[#121212] text-[#ff72ad] shadow-lg transition-all hover:brightness-110 disabled:opacity-70 active:scale-[0.98]"
                      aria-label="Beğen"
                    >
                      <Heart className="h-7 w-7 fill-current" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Card className="rounded-3xl border-slate-200 bg-white p-6 text-sm text-slate-600 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900/40 dark:text-slate-400">
                Keşfedecek profil yok.
              </Card>
            )
          ) : activeTab === "begeniler" ? (
            activeBegenilerTab === "liked" ? (
              shouldBlurReceivedLikes ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    {(displayedLikesCards.length ? displayedLikesCards : likesCards).slice(0, 4).map((item: any, index: number) => (
                      <div key={item?.user?.id ?? `locked-${index}`} className="relative aspect-[3/4] overflow-hidden rounded-2xl">
                        <img
                          src={item?.user?.photos?.[0]?.url || "/placeholder.svg"}
                          alt="Kilitli profil"
                          className="h-full w-full scale-110 object-cover blur-2xl opacity-70"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[var(--panel-primary)]/35 to-transparent" />
                        <div className="absolute inset-0 grid place-items-center">
                          <span className="grid size-12 place-items-center rounded-full bg-black/20 text-white/70 backdrop-blur-sm">
                            <Lock className="h-6 w-6" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-3xl border border-[var(--panel-primary)]/20 bg-white/90 p-5 shadow-[0_20px_40px_rgba(112,42,225,0.12)] dark:bg-[#1a1330]/95">
                    <div className="flex items-center gap-4">
                      <div className="grid size-12 place-items-center rounded-xl bg-gradient-to-br from-[var(--panel-primary)] to-[#b28cff] text-white">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-extrabold text-slate-900 dark:text-slate-100">Premium'a Geç</div>
                        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">Seni beğenenleri gör ve sınırsız keşfin tadını çıkar.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate("/panel/magaza?from=likes-blur-upgrade")}
                        className="rounded-full bg-gradient-to-br from-[var(--panel-primary)] to-[#b28cff] px-4 py-2 text-xs font-extrabold text-white shadow-sm transition hover:brightness-110"
                      >
                        Yükselt
                      </button>
                    </div>
                  </div>
                </div>
              ) : displayedLikesCards.length ? (
                <div className="space-y-3">
                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">{displayedLikesCards.length} kişi seni beğendi.</div>
                  <div className="grid grid-cols-1 gap-5 pt-1 md:grid-cols-2 xl:grid-cols-3">
                    {displayedLikesCards.map((item: any) => (
                      <LikesShowcaseCard
                        key={item.user.id}
                        entry={item}
                        onConnect={() => onConnectFromLikes(item)}
                        onOpenReport={() => openCompatibilityReport(item)}
                        onOpenProfile={() => openProfileFromLikes(item)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <Card className="rounded-[2rem] border-[var(--panel-primary)]/20 bg-[#160f27]/70 p-6 text-sm text-slate-300 dark:border-[var(--panel-primary)]/20 dark:bg-[#160f27]/70">
                  {likesCards.length ? "Filtreye uygun beğeni bulunamadı." : "Seni beğenen kullanıcı henüz yok."}
                </Card>
              )
            ) : begenilerMatches.length ? (
              <div className="space-y-6">
                <section>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">Yeni Eşleşmeler</h3>
                    <span className="rounded-full bg-[var(--panel-primary)]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--panel-primary)]">
                      {begenilerMatches.length} yeni
                    </span>
                  </div>
                  <div className="-mx-1 flex gap-4 overflow-x-auto px-1 pb-3">
                    {begenilerMatches.slice(0, 12).map((item: any) => (
                      <button key={`match-chip-${item.user.id}`} type="button" onClick={() => openPrivateChat(item)} className="group shrink-0 text-center">
                        <div className="rounded-full bg-gradient-to-br from-[var(--panel-primary)] to-[#b28cff] p-1">
                          <div className="size-20 overflow-hidden rounded-full border-4 border-[#fbf4ff]">
                            <img src={getPrimaryPhotoUrl(item.user)} alt={item.user.displayName} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                          </div>
                        </div>
                        <p className="mt-2 max-w-20 truncate text-xs font-semibold text-slate-800 dark:text-slate-200">{item.user.displayName.split(" ")[0]}</p>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="space-y-3">
                  <h3 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">Eşleşme Listesi</h3>
                  <div className="grid grid-cols-1 gap-5 pt-1 md:grid-cols-2 xl:grid-cols-3">
                    {begenilerMatches.map((item: any) => (
                      <LikesShowcaseCard
                        key={`match-card-${item.user.id}`}
                        entry={item}
                        onConnect={() => openPrivateChat(item)}
                        onOpenReport={() => openCompatibilityReport(item)}
                        onOpenProfile={() => openProfileFromLikes(item)}
                      />
                    ))}
                  </div>
                </section>
              </div>
            ) : (
              <Card className="rounded-[2rem] border-[var(--panel-primary)]/20 bg-[#160f27]/70 p-6 text-sm text-slate-300 dark:border-[var(--panel-primary)]/20 dark:bg-[#160f27]/70">
                Henüz karşılıklı eşleşme görünmüyor.
              </Card>
            )
          ) : activeTab === "uyum" ? (
            compatibilityDetail ? (
              <div className="space-y-6 rounded-[2rem] border border-[var(--panel-primary)]/15 bg-[#120c22]/90 p-5 md:p-8">
                <div className="grid gap-6 md:grid-cols-[1fr_240px_1fr] md:items-center">
                  <div className="text-center">
                    <img src={compatibilityDetail.me.photo} alt={compatibilityDetail.me.name} className="mx-auto size-28 rounded-full border-4 border-[var(--panel-primary)]/40 object-cover" loading="lazy" />
                    <div className="mt-3 text-3xl font-extrabold text-white">{compatibilityDetail.me.name}</div>
                    <div className="text-sm font-semibold text-[var(--panel-primary)]">Yaşam Yolu: {compatibilityDetail.me.destiny}</div>
                    <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">Ruh: {compatibilityDetail.me.soul} • Kader: {compatibilityDetail.me.expression}</div>
                  </div>

                  <div className="relative mx-auto grid size-[220px] place-items-center">
                    <div className="absolute inset-0 rounded-full border-2 border-dashed border-[var(--panel-primary)]/35" />
                    <div className="absolute inset-6 rounded-full border border-[var(--panel-primary)]/35" />
                    <div className="relative z-10 text-center">
                      <div className="text-6xl font-black text-transparent bg-gradient-to-br from-white to-[var(--panel-primary)] bg-clip-text">%{compatibilityDetail.overview.scores.dailyHarmony}</div>
                      <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[var(--panel-primary)]/80">Sinerji Skoru</div>
                    </div>
                    <div className="absolute -bottom-2 rounded-full bg-[var(--panel-primary)] px-4 py-1 text-xs font-bold text-white shadow-lg shadow-[var(--panel-primary)]/35">{compatibilityDetail.overview.categoryLabel}</div>
                  </div>

                  <div className="text-center">
                    <img src={compatibilityDetail.target.photo} alt={compatibilityDetail.target.name} className="mx-auto size-28 rounded-full border-4 border-[var(--panel-primary)]/40 object-cover" loading="lazy" />
                    <div className="mt-3 text-3xl font-extrabold text-white">{compatibilityDetail.target.name}</div>
                    <div className="text-sm font-semibold text-[var(--panel-primary)]">Yaşam Yolu: {compatibilityDetail.target.destiny}</div>
                    <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">Ruh: {compatibilityDetail.target.soul} • Kader: {compatibilityDetail.target.expression}</div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-3xl border border-[var(--panel-primary)]/15 bg-[var(--panel-primary)]/8 p-5">
                    <div className="text-xs font-black uppercase tracking-[0.2em] text-[var(--panel-primary)]">Yaşam Yolu Uyumu</div>
                    <div className="mt-3 flex items-center gap-3 text-3xl font-black text-white">
                      <span>{compatibilityDetail.me.destiny}</span>
                      <div className="h-1 flex-1 rounded-full bg-[var(--panel-primary)]/20"><div className="h-full rounded-full bg-[var(--panel-primary)]" style={{ width: `${compatibilityDetail.scores.lifePathHarmony}%` }} /></div>
                      <span>{compatibilityDetail.target.destiny}</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-300">{compatibilityDetail.overview.notes[0]}</p>
                  </div>

                  <div className="rounded-3xl border border-[var(--panel-primary)]/15 bg-[var(--panel-primary)]/8 p-5">
                    <div className="text-xs font-black uppercase tracking-[0.2em] text-[var(--panel-primary)]">Ruh Güdüsü Uyumu</div>
                    <div className="mt-3 flex items-center gap-3 text-3xl font-black text-white">
                      <span>{compatibilityDetail.me.soul}</span>
                      <div className="h-1 flex-1 rounded-full bg-[var(--panel-primary)]/20"><div className="h-full rounded-full bg-[var(--panel-primary)]" style={{ width: `${compatibilityDetail.scores.soulHarmony}%` }} /></div>
                      <span>{compatibilityDetail.target.soul}</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-300">{compatibilityDetail.overview.notes[1] ?? compatibilityDetail.overview.notes[0]}</p>
                  </div>
                </div>

                {hasDetailedCompatibility ? (
                  <>
                    <div className="grid gap-6 rounded-3xl border border-[var(--panel-primary)]/15 bg-[var(--panel-primary)]/6 p-5 md:grid-cols-[260px_1fr]">
                      <div className="mx-auto grid size-56 place-items-center">
                        <svg viewBox="0 0 100 100" className="size-full">
                          <polygon points="50,8 90,34 76,86 24,86 10,34" fill="none" stroke="rgba(144,84,248,0.25)" strokeWidth="1" />
                          <polygon points="50,20 82,40 70,78 30,78 18,40" fill="rgba(144,84,248,0.15)" stroke="rgba(144,84,248,0.85)" strokeWidth="1.5" />
                        </svg>
                      </div>
                      <div>
                        <div className="mb-4 text-3xl font-extrabold text-white">Uyum Boyutları</div>
                        {[
                          ["Ruhsal Rezonans", compatibilityDetail.scores.spiritual],
                          ["Duygusal Derinlik", compatibilityDetail.scores.emotional],
                          ["Zihinsel Senkronizasyon", compatibilityDetail.scores.mental],
                          ["Fiziksel Enerji", compatibilityDetail.scores.physical],
                          ["Kozmik Akış", compatibilityDetail.scores.cosmic],
                        ].map(([label, value]) => (
                          <div key={String(label)} className="flex items-center justify-between border-b border-white/10 py-2 text-sm">
                            <span className="text-slate-300">{String(label)}</span>
                            <span className="font-bold text-[var(--panel-primary)]">%{Number(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-[var(--panel-primary)]/25 bg-gradient-to-br from-[var(--panel-primary)]/18 to-transparent p-6">
                      <div className="text-2xl font-extrabold text-white">Kozmik Mesaj</div>
                      <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-200 md:text-base">
                        {compatibilityDetail.overview.notes.slice(0, 6).map((note, idx) => {
                          const sep = note.indexOf(":");
                          const hasTitle = sep > 0;
                          const title = hasTitle ? note.slice(0, sep).trim() : "Yorum";
                          const body = hasTitle ? note.slice(sep + 1).trim() : note;
                          return (
                            <div key={`${title}-${idx}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                              <div className="text-xs font-extrabold uppercase tracking-[0.12em] text-[var(--panel-primary)]">{title}</div>
                              <p className="mt-1">{body}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-3xl border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/10 p-5 text-sm text-slate-200">
                    Detaylı uyum boyutları ve kozmik yorumlar Pro paketinde açılır.
                  </div>
                )}

                <div className="grid gap-3 md:grid-cols-3">
                  <button
                    type="button"
                    onClick={messageFromCompatibility}
                    className="rounded-full bg-[var(--panel-primary)] py-3 text-sm font-extrabold text-white shadow-lg shadow-[var(--panel-primary)]/30"
                  >
                    {getDisplayFirstName(compatibilityDetail.targetUser.displayName)} ile Bağ Kur
                  </button>
                  <button
                    type="button"
                    onClick={connectFromCompatibility}
                    disabled={likeMutation.isPending}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/10 py-3 text-sm font-extrabold text-[var(--panel-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Heart className={cn("h-4 w-4", isCompatibilityLiked ? "fill-red-500 text-red-500" : "text-[var(--panel-primary)]")} />
                    {isCompatibilityLiked ? "Beğendin" : "Beğen"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void shareCompatibility();
                    }}
                    className="rounded-full border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/10 py-3 text-sm font-extrabold text-[var(--panel-primary)]"
                  >
                    Paylaş
                  </button>
                </div>
              </div>
            ) : (
              <Card className="rounded-3xl border-[var(--panel-primary)]/20 bg-[#160f27]/80 p-6 text-sm text-slate-300">
                Uyum raporu yüklenemedi.
              </Card>
            )
          ) : activeTab === "profil" ? (
            profileDetail ? (
              <div className="space-y-6 rounded-[2rem] bg-slate-50/85 p-5 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12),0_10px_25px_rgba(15,23,42,0.08)] backdrop-blur-sm md:p-8 dark:bg-slate-900/35">
                <div className="grid gap-6 md:grid-cols-[220px_1fr] md:items-center">
                  <img
                    src={getPrimaryPhotoUrl(profileDetail.user)}
                    alt={profileDetail.user.displayName}
                    className="mx-auto size-40 rounded-full border-4 border-[var(--panel-primary)]/35 object-cover"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-4xl font-extrabold text-slate-900 dark:text-slate-100">{profileDetail.user.displayName}, {profileDetail.age}</div>
                    <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">{profileDetail.user.bio?.trim() || "Bu kullanıcı henüz biyografi eklemedi."}</div>
                    <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                      <span className="rounded-full bg-[var(--panel-primary)]/20 px-3 py-1 text-[var(--panel-primary)]">Yol Sayısı {profileDetail.numerology.destinyNumber}</span>
                      <span className="rounded-full bg-[var(--panel-primary)]/20 px-3 py-1 text-[var(--panel-primary)]">Ruh Arzusu {profileDetail.numerology.heartDesireNumber}</span>
                      <span className="rounded-full bg-[var(--panel-primary)]/20 px-3 py-1 text-[var(--panel-primary)]">İfade {profileDetail.numerology.expressionNumber}</span>
                      <span className="rounded-full bg-[var(--panel-primary)]/20 px-3 py-1 text-[var(--panel-primary)]">Kişilik {profileDetail.numerology.personalityNumber}</span>
                      <span className="rounded-full bg-[var(--panel-primary)]/20 px-3 py-1 text-[var(--panel-primary)]">Doğum Günü {profileDetail.numerology.birthDayNumber}</span>
                      <span className="rounded-full bg-[var(--panel-primary)]/20 px-3 py-1 text-[var(--panel-primary)]">Arketip {profileDetail.archetype.name}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl bg-white/65 p-4 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.10)] dark:bg-slate-900/35">
                  <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-900 dark:text-slate-100">Ruhsal Yolculuk Rozetleri</div>
                  <div className="mt-3 flex gap-4 overflow-x-auto pb-1">
                    {profileDetail.journeyBadges.map((badge) => (
                      <div key={badge.key} className="flex min-w-[90px] shrink-0 flex-col items-center gap-2">
                        <div className="grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#efe6ff] to-[#dbc7ff] text-sm font-black text-[var(--panel-primary)] shadow-sm">
                          {badge.short}
                        </div>
                        <span className="text-center text-[11px] font-semibold leading-tight text-slate-700 dark:text-slate-200">{badge.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--panel-primary)]/18 via-[var(--panel-primary)]/8 to-transparent p-5 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12),0_8px_20px_rgba(15,23,42,0.06)]">
                    <div className="pointer-events-none absolute -right-16 -top-16 size-44 rounded-full bg-[var(--panel-primary)]/30 blur-3xl" />
                    <div className="relative">
                      <div className="inline-flex items-center gap-2 rounded-full border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[var(--panel-primary)]">
                        <Sparkles className="h-3.5 w-3.5" />
                        Arketip
                      </div>
                      <div className="mt-3 text-2xl font-black text-slate-900 md:text-3xl dark:text-slate-100">{profileDetail.archetype.name}</div>
                      <p className="mt-2 text-sm leading-relaxed text-slate-700 md:text-base dark:text-slate-200">{profileDetail.archetypeSpotlight}</p>
                      <div className="mt-3 inline-flex rounded-full border border-[var(--panel-primary)]/20 bg-[var(--panel-primary)]/8 px-3 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        Denge pratiği: {profileDetail.archetype.practice}
                      </div>
                    </div>
                  </div>

                  {profileDetail.photos.length > 1 ? (
                    <div className="overflow-hidden rounded-2xl border border-[var(--panel-primary)]/15 bg-white/70 p-3 dark:bg-slate-900/30">
                      <img src={profileDetail.photos[1]} alt={`${profileDetail.user.displayName} profil fotoğrafı`} className="h-52 w-full rounded-xl object-cover" loading="lazy" />
                    </div>
                  ) : null}

                  <div className="rounded-2xl bg-white/65 p-4 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.10)] dark:bg-slate-900/35">
                    <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-900 dark:text-slate-100">Numeroloji Özeti</div>
                    <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                      <div className="flex items-center justify-between gap-3"><span className="text-slate-500 dark:text-slate-400">Kader yolu / sayısı</span><span className="font-bold text-slate-900 dark:text-slate-100">{profileDetail.numerology.destinyPath} • {profileDetail.numerology.destinyNumber}</span></div>
                      <div className="flex items-center justify-between gap-3"><span className="text-slate-500 dark:text-slate-400">Kalp arzusu</span><span className="font-bold">{profileDetail.numerology.heartDesireNumber}</span></div>
                      <div className="flex items-center justify-between gap-3"><span className="text-slate-500 dark:text-slate-400">İfade</span><span className="font-bold">{profileDetail.numerology.expressionNumber}</span></div>
                      <div className="flex items-center justify-between gap-3"><span className="text-slate-500 dark:text-slate-400">Kişilik</span><span className="font-bold">{profileDetail.numerology.personalityNumber}</span></div>
                      <div className="flex items-center justify-between gap-3"><span className="text-slate-500 dark:text-slate-400">Doğum günü numarası</span><span className="font-bold">{profileDetail.numerology.birthDayNumber}</span></div>
                      <div className="flex items-center justify-between gap-3"><span className="text-slate-500 dark:text-slate-400">Karmik kodlar</span><span className="font-bold">{profileDetail.numerology.karmicCodes.length ? profileDetail.numerology.karmicCodes.join(", ") : "—"}</span></div>
                    </div>

                    <div className="mt-4 rounded-xl bg-white/80 p-3 text-sm text-slate-700 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.10)] dark:bg-slate-900/45 dark:text-slate-200">
                      <div className="text-xs font-black uppercase tracking-[0.12em] text-slate-900 dark:text-slate-100">Kısa Kişilik Yorumu</div>
                      <p className="mt-2 text-slate-600 dark:text-slate-300">{profileDetail.quickSummary}</p>
                      <div className="mt-3 space-y-2">
                        <p><span className="font-semibold text-[var(--panel-primary)]">{profileDetail.numerology.destinyPath}:</span> {profileDetail.destinyInsight}</p>
                        <p><span className="font-semibold text-[var(--panel-primary)]">Kalp Arzusu {profileDetail.numerology.heartDesireNumber}:</span> {profileDetail.heartInsight}</p>
                        <p><span className="font-semibold text-[var(--panel-primary)]">İfade {profileDetail.numerology.expressionNumber}:</span> {profileDetail.expressionInsight}</p>
                        <p><span className="font-semibold text-[var(--panel-primary)]">Kişilik {profileDetail.numerology.personalityNumber}:</span> {profileDetail.personalityInsight}</p>
                        <p><span className="font-semibold text-[var(--panel-primary)]">Doğum Günü {profileDetail.numerology.birthDayNumber}:</span> {profileDetail.birthdayInsight}</p>
                        {profileDetail.karmicInsights.length ? (
                          <div>
                            <div className="font-semibold text-[var(--panel-primary)]">Karmik Kod Yorumu</div>
                            <div className="mt-1 space-y-1 text-slate-600 dark:text-slate-300">
                              {profileDetail.karmicInsights.map((line) => (
                                <p key={line}>{line}</p>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {profileDetail.photos.length > 2 ? (
                    <div className="grid grid-cols-2 gap-2 overflow-hidden rounded-2xl border border-[var(--panel-primary)]/15 bg-white/70 p-3 dark:bg-slate-900/30">
                      {profileDetail.photos.slice(2, 4).map((photoUrl, index) => (
                        <img key={`${profileDetail.user.id}-story-photo-${index}`} src={photoUrl} alt={`${profileDetail.user.displayName} ek fotoğraf ${index + 3}`} className="h-36 w-full rounded-xl object-cover" loading="lazy" />
                      ))}
                    </div>
                  ) : null}

                  {profileDetail.interests.length ? (
                    <div className="rounded-2xl bg-white/65 p-4 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.10)] dark:bg-slate-900/35">
                      <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-900 dark:text-slate-100">İlgi Alanları</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {profileDetail.interests.map((interest) => (
                          <span key={interest} className="rounded-full border border-[var(--panel-primary)]/25 bg-[var(--panel-primary)]/10 px-3 py-1 text-xs font-semibold text-[var(--panel-primary)]">{interest}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => openCompatibilityReport({ user: profileDetail.user })}
                    className="rounded-full bg-[var(--panel-primary)] py-3 text-sm font-extrabold text-white shadow-lg shadow-[var(--panel-primary)]/30"
                  >
                    Uyum Raporunu Gör
                  </button>
                  <button
                    type="button"
                    onClick={() => openPrivateChat({ user: profileDetail.user })}
                    className="rounded-full border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/10 py-3 text-sm font-extrabold text-[var(--panel-primary)]"
                  >
                    Mesaj Gönder
                  </button>
                </div>
              </div>
            ) : (
              <Card className="rounded-3xl bg-slate-50/85 p-6 text-sm text-slate-600 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12)] dark:bg-slate-900/35 dark:text-slate-300">
                Profil bulunamadı.
              </Card>
            )
          ) : activeTab === "sohbet" ? (
            hasSelectedSohbet && selectedSohbetMatch && selectedSohbetOtherUser ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border border-white/10 bg-white/[0.03] shadow-[0_24px_55px_rgba(0,0,0,0.25)] sm:rounded-[2rem]">
                <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.02] px-4 py-4 md:px-6">
                  <div className="flex min-w-0 items-center gap-3">
                    <button
                      type="button"
                      onClick={closeSohbetDetail}
                      className="grid size-10 shrink-0 place-items-center rounded-full border border-white/15 bg-white/5 text-slate-200 transition hover:bg-white/10"
                      aria-label="Sohbet listesine dön"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/panel/kesfet/profil/${encodeURIComponent(selectedSohbetOtherUser.id)}`)}
                      className="group flex min-w-0 items-center gap-3 text-left"
                      aria-label={`${selectedSohbetOtherUser.displayName} profilini aç`}
                    >
                      <img
                        src={getPrimaryPhotoUrl(selectedSohbetOtherUser)}
                        alt={selectedSohbetOtherUser.displayName}
                        className="size-11 shrink-0 rounded-full border border-white/20 object-cover transition group-hover:border-[var(--panel-primary)]"
                        loading="lazy"
                      />
                      <div className="min-w-0">
                        <div className="truncate text-base font-bold text-white transition group-hover:text-[var(--panel-primary)]">{selectedSohbetOtherUser.displayName}</div>
                        <div className="truncate text-xs font-medium text-slate-400">{selectedSohbetTitle}</div>
                        {selectedSohbetConversationKeys.some((key) => Boolean(typingMap[key])) ? (
                          <div className="text-[11px] font-semibold text-[var(--panel-primary)]">yazıyor...</div>
                        ) : null}
                      </div>
                    </button>
                  </div>

                  <div className="relative flex items-center gap-1.5 text-slate-300" data-chat-menu-root="header">
                    <button
                      type="button"
                      className="grid size-9 place-items-center rounded-full transition hover:bg-white/10"
                      aria-label="Diğer seçenekler"
                      onClick={() => setShowSohbetHeaderMenu((prev) => !prev)}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>

                    {showSohbetHeaderMenu ? (
                      <div className="absolute right-0 top-10 z-20 min-w-[180px] rounded-xl border border-white/15 bg-[#1b1530] p-1.5 shadow-xl">
                        <button
                          type="button"
                          className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-100 transition hover:bg-white/10"
                          onClick={() => {
                            if (selectedSohbetPeerId) handleDeleteConversation(selectedSohbetPeerId);
                          }}
                        >
                          Yazışmayı Sil
                        </button>
                        <button
                          type="button"
                          className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-rose-300 transition hover:bg-rose-500/15"
                          onClick={() => {
                            if (selectedSohbetPeerId) handleBlockConversationUser(selectedSohbetPeerId);
                          }}
                        >
                          Engelle
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-transparent px-4 py-5 md:px-6 md:py-6">
                  {selectedSohbetThread.length ? (
                    <div ref={sohbetThreadRef} className="mb-4 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pr-1 pt-2">
                      <div className={cn("flex flex-col gap-4", selectedSohbetThread.length <= 6 ? "mt-auto" : "")}>
                        {selectedSohbetThread.map((message: any, index: number) => {
                          const mine = message.fromUserId === realtimeMeId || message.fromUserId === me.id;
                          const shouldOpenMessageMenuUpward = index >= selectedSohbetThread.length - 2;
                          return (
                            <div key={message.id} className={mine ? "flex justify-end" : "flex justify-start"}>
                              <div className="max-w-[88%]">
                                <div
                                  className={cn(
                                    "rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm break-words whitespace-pre-wrap",
                                    mine
                                      ? "rounded-br-md border border-[#e5def7] bg-white text-slate-900"
                                      : "rounded-bl-md border border-[#e5def7] bg-white text-slate-900",
                                  )}
                                >
                                  {mine && editingMessageId === String(message.id ?? "") ? (
                                    <div className="space-y-2">
                                      <textarea
                                        value={editingMessageText}
                                        onChange={(event) => setEditingMessageText(event.target.value)}
                                        className="min-h-[74px] w-full rounded-xl border border-[#e5def7] bg-white px-3 py-2 text-sm text-slate-900 outline-none"
                                      />
                                      <div className="flex justify-end gap-2">
                                        <button
                                          type="button"
                                          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
                                          onClick={() => {
                                            setEditingMessageId("");
                                            setEditingMessageText("");
                                          }}
                                        >
                                          Vazgeç
                                        </button>
                                        <button
                                          type="button"
                                          className="rounded-full bg-[var(--panel-primary)] px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
                                          disabled={editSohbetMessageMutation.isPending || !editingMessageText.trim()}
                                          onClick={() => editSohbetMessageMutation.mutate({ messageId: String(message.id ?? ""), text: editingMessageText })}
                                        >
                                          Kaydet
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>{message.text}</div>
                                  )}
                                </div>
                                <div className={cn("mt-1 flex items-center gap-2 px-1 text-[11px] text-slate-600", mine ? "justify-end text-right" : "justify-start text-left")}>
                                  <span>{formatSohbetTime(message.createdAt)}</span>
                                  {mine ? (
                                    <div className="relative" data-chat-menu-root="message">
                                      <button
                                        type="button"
                                        onClick={() => setActiveMessageMenuId((prev) => (prev === String(message.id ?? "") ? "" : String(message.id ?? "")))}
                                        className="rounded-full p-1 text-slate-500 transition hover:bg-slate-100"
                                        aria-label="Mesaj seçenekleri"
                                      >
                                        <MoreVertical className="h-3.5 w-3.5" />
                                      </button>
                                      {activeMessageMenuId === String(message.id ?? "") ? (
                                        <div
                                          className={cn(
                                            "absolute right-0 z-20 min-w-[145px] rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg",
                                            shouldOpenMessageMenuUpward ? "bottom-6" : "top-6",
                                          )}
                                        >
                                          <button
                                            type="button"
                                            className="w-full rounded-md px-2 py-1.5 text-left text-[11px] font-medium text-slate-700 hover:bg-slate-100"
                                            onClick={() => handleEditSohbetMessage({ id: String(message.id ?? ""), text: String(message.text ?? "") })}
                                          >
                                            Düzenle
                                          </button>
                                          <button
                                            type="button"
                                            className="w-full rounded-md px-2 py-1.5 text-left text-[11px] font-medium text-amber-700 hover:bg-amber-50"
                                            disabled={deleteSohbetMessageMutation.isPending}
                                            onClick={() => handleRetractSohbetMessage(String(message.id ?? ""))}
                                          >
                                            Geri Al
                                          </button>
                                          <button
                                            type="button"
                                            className="w-full rounded-md px-2 py-1.5 text-left text-[11px] font-medium text-rose-700 hover:bg-rose-50"
                                            onClick={() => handleLocalHideSohbetMessage(String(message.id ?? ""))}
                                          >
                                            Sil
                                          </button>
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-5 rounded-2xl border border-dashed border-white/20 bg-white/[0.04] px-4 py-6 text-center text-sm text-slate-300">
                      Sohbeti başlatmak için ilk mesajı gönder.
                    </div>
                  )}

                  {selectedSohbetThread.length === 0 ? (
                    <div className="mb-4 max-h-[22vh] space-y-2 overflow-y-auto pr-1">
                      {[
                        "Merhaba! Enerjini çok güzel hissettim.",
                        "Bugün nasılsın? Sohbeti devam ettirmek isterim.",
                      ].map((quick) => (
                        <button
                          key={quick}
                          type="button"
                          onClick={() => setSohbetMessage(quick)}
                          className="w-full rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 text-left text-sm font-medium text-slate-200 transition hover:bg-white/[0.08]"
                        >
                          {quick}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  <div className="relative flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-3 py-2.5 shadow-[0_10px_25px_rgba(0,0,0,0.18)]">
                    {emojiBursts.length ? (
                      <div className="pointer-events-none absolute -top-3 left-4 z-20">
                        {emojiBursts.map((burst) => (
                          <motion.span
                            key={burst.id}
                            initial={{ opacity: 0, y: 8, x: 0, scale: 0.85 }}
                            animate={{ opacity: 1, y: -42, x: burst.x, scale: 1.2 }}
                            transition={{ duration: 0.85, ease: "easeOut" }}
                            className="absolute text-xl"
                          >
                            {burst.emoji}
                          </motion.span>
                        ))}
                      </div>
                    ) : null}

                    <div className="relative" data-chat-menu-root="emoji">
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        className={cn("grid size-8 place-items-center rounded-full text-slate-400 transition hover:bg-white/10", showEmojiPicker ? "bg-white/12 text-white" : "")}
                        aria-label="Emoji seç"
                      >
                        <Smile className="h-4 w-4" />
                      </button>

                      {showEmojiPicker ? (
                        <div className="absolute bottom-11 left-0 z-30 w-[230px] rounded-2xl border border-white/15 bg-[#1b1530]/95 p-2 shadow-2xl backdrop-blur-sm">
                          <div className="grid grid-cols-6 gap-1">
                            {SOHBET_EMOJI_SET.map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => handleSelectEmoji(emoji)}
                                className="grid h-8 w-8 place-items-center rounded-lg text-lg transition hover:bg-white/10"
                                aria-label={`${emoji} ekle`}
                              >
                                <motion.span whileHover={{ scale: 1.18, y: -2 }} whileTap={{ scale: 0.92 }}>
                                  {emoji}
                                </motion.span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <input
                      value={sohbetMessage}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSohbetMessage(value);

                        if (!selectedSohbetOtherUser || !selectedSohbetConversationKey) return;

                        if (!value.trim()) {
                          if (typingStopBroadcastRef.current) {
                            clearTimeout(typingStopBroadcastRef.current);
                            typingStopBroadcastRef.current = null;
                          }
                          if (isTypingBroadcastedRef.current) {
                            isTypingBroadcastedRef.current = false;
                            void emitTypingState(false);
                          }
                          return;
                        }

                        if (!isTypingBroadcastedRef.current) {
                          isTypingBroadcastedRef.current = true;
                          void emitTypingState(true);
                        }

                        if (typingStopBroadcastRef.current) {
                          clearTimeout(typingStopBroadcastRef.current);
                        }

                        typingStopBroadcastRef.current = setTimeout(() => {
                          if (!isTypingBroadcastedRef.current) return;
                          isTypingBroadcastedRef.current = false;
                          void emitTypingState(false);
                        }, 1200);
                      }}
                      onBlur={() => {
                        if (typingStopBroadcastRef.current) {
                          clearTimeout(typingStopBroadcastRef.current);
                          typingStopBroadcastRef.current = null;
                        }
                        if (!isTypingBroadcastedRef.current) return;
                        isTypingBroadcastedRef.current = false;
                        void emitTypingState(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void sendSohbetMessage();
                      }}
                      placeholder="Mesaj yaz..."
                      className="h-10 flex-1 border-0 bg-transparent text-base font-medium text-white outline-none placeholder:text-slate-400 md:text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => void sendSohbetMessage()}
                      className="grid size-10 place-items-center rounded-full bg-[var(--panel-primary)] text-white transition hover:brightness-110"
                      aria-label="Mesaj gönder"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <section>
                  <div className="mb-4 flex items-end justify-between">
                    <div className="text-2xl font-black tracking-tight text-[#12172b]">Yeni Eşleşmeler</div>
                    <div className="flex items-center gap-2">
                      {filteredSohbetList.length > 8 ? (
                        <button
                          type="button"
                          onClick={() => setShowAllNewMatches((prev) => !prev)}
                          className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--panel-primary)]"
                        >
                          {showAllNewMatches ? "Daha Az Görüntüle" : "Tümünü Görüntüle"}
                        </button>
                      ) : null}
                      <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--panel-primary)]">
                        Toplam: {filteredSohbetList.length}
                      </div>
                    </div>
                  </div>
                  {visibleNewMatches.length ? (
                    <div className="no-scrollbar -mx-5 flex gap-4 overflow-x-auto px-5 pb-3">
                      {visibleNewMatches.map((item: any, idx: number) => (
                        <button
                          type="button"
                          key={item.user.id}
                          onClick={() => openPrivateChat(item)}
                          className="group flex shrink-0 flex-col items-center gap-2 text-center"
                        >
                          <div className="relative rounded-full bg-gradient-to-br from-[var(--panel-primary)] to-[#e38f68] p-1">
                            <div className="size-20 overflow-hidden rounded-full border-4 border-[#fbf4ff]">
                              <img src={getPrimaryPhotoUrl(item.user)} alt={item.user.displayName} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                            </div>
                            <div className="absolute bottom-1 right-1 rounded-full bg-[var(--panel-primary)] px-1.5 py-0.5 text-[10px] font-bold text-white">
                              %{item.overview.scores.dailyHarmony}
                            </div>
                          </div>
                          <div className="max-w-20 truncate text-xs font-semibold text-slate-800">{item.user.displayName.split(" ")[0]}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <Card className="rounded-2xl border-[#e1d7ea] bg-[#f4edf8] p-4 text-sm text-slate-500">
                      Henüz yeni eşleşme görünmüyor.
                    </Card>
                  )}
                </section>

                <section>
                  <div className="mb-3 text-2xl font-black tracking-tight text-[#12172b]">Son Sohbetler</div>
                  {recentChats.length ? (
                    <div className="space-y-2">
                      {recentChats.map((item: any, idx: number) => {
                        const conversationKey = getConversationKey(realtimeMeId, item.user.id);
                        const unreadCount = sohbetUnreadMap[conversationKey] ?? 0;

                        return (
                          <div
                            key={item.user.id}
                            className={cn(
                              "flex items-center gap-3 rounded-2xl px-4 py-3 transition",
                              unreadCount > 0
                                ? "bg-white shadow-[0_16px_32px_rgba(112,42,225,0.08)]"
                                : "bg-[#f4edf8] hover:bg-[#efe6f6]",
                            )}
                          >
                            <button type="button" onClick={() => openPrivateChat(item)} className="flex min-w-0 flex-1 items-center gap-4 text-left">
                              <div className="relative shrink-0">
                                <img src={getPrimaryPhotoUrl(item.user)} alt={item.user.displayName} className="size-14 rounded-full object-cover" loading="lazy" />
                                {idx === 0 ? <span className="absolute bottom-0 right-0 size-3 rounded-full bg-emerald-400 ring-2 ring-slate-50" /> : null}
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="mb-1 flex items-center justify-between gap-3">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <div className="truncate text-base font-bold text-[#12172b]">{item.user.displayName}</div>
                                    <span className="rounded bg-[var(--panel-primary)]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--panel-primary)]">
                                      {item.overview.categoryLabel}
                                    </span>
                                  </div>
                                  <span className="text-xs font-semibold text-[var(--panel-primary)]">{item._timeText || "--:--"}</span>
                                </div>
                                <div className="line-clamp-1 text-sm font-medium leading-relaxed text-slate-500">
                                  {item._previewText}
                                </div>
                              </div>

                              {unreadCount > 0 ? (
                                <div className="grid min-h-5 min-w-5 place-items-center rounded-full bg-[var(--panel-primary)] px-1 text-[10px] font-bold text-white">
                                  {Math.min(unreadCount, 99)}
                                </div>
                              ) : null}
                            </button>

                            <div className="relative shrink-0" data-chat-menu-root="recent">
                              <button
                                type="button"
                                className="grid size-9 place-items-center rounded-full text-slate-500 transition hover:bg-white"
                                aria-label="Sohbet seçenekleri"
                                onClick={() => setActiveRecentChatMenuPeerId((prev) => (prev === item.user.id ? "" : item.user.id))}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </button>
                              {activeRecentChatMenuPeerId === item.user.id ? (
                                <div className="absolute right-0 top-10 z-20 min-w-[165px] rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl dark:border-white/15 dark:bg-[#1b1530]">
                                  <button
                                    type="button"
                                    className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/10"
                                    onClick={() => handleDeleteConversation(item.user.id)}
                                  >
                                    Yazışmayı Sil
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-rose-600 transition hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-500/15"
                                    onClick={() => handleBlockConversationUser(item.user.id)}
                                  >
                                    Engelle
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Card className="rounded-2xl border-[#e1d7ea] bg-[#f4edf8] p-4 text-sm text-slate-500">
                      Karşılıklı beğeni olunca burada özel sohbet listelenir.
                    </Card>
                  )}
                </section>
              </div>
            )
          ) : (
            <Card className="rounded-3xl border-slate-200 bg-white p-6 text-sm text-slate-600 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900/40 dark:text-slate-400">
              Karşılıklı beğeni olunca burada özel sohbet listelenir.
            </Card>
          )}
        </section>

        {activeTab === "kesfet" ? null : activeTab === "sohbet" && !hasSelectedSohbet ? (
          <aside className="hidden min-w-0 space-y-6">
            <div className="rounded-3xl bg-slate-50/85 p-6 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.12),0_10px_25px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:bg-slate-900/35">
              <div className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900 dark:text-slate-100">
                <Sparkles className="h-4 w-4 text-[var(--panel-primary)]" /> Günün Uyumu
              </div>
              <div className="mx-auto grid size-28 place-items-center rounded-full border-4 border-[var(--panel-primary)]/25 text-3xl font-black text-[var(--panel-primary)]">
                %{sohbetDailyHarmony || 84}
              </div>
              <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-400">
                Bugün sosyal ilişkilerinizde benzer frekanstaki kişilerle daha akışkan iletişim kurabilirsiniz.
              </p>
            </div>

            <div>
              <div className="mb-3 text-lg font-bold text-white">Popüler Etiketler</div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-[var(--panel-primary)]/15 px-3 py-1 text-xs font-semibold text-[var(--panel-primary)]">#KarmikBağ</span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-slate-300">#RuhEşi</span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-slate-300">#EnerjiUyumu</span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-slate-300">#AşkNumerolojisi</span>
              </div>
            </div>

            <div className="rounded-3xl bg-gradient-to-br from-[var(--panel-primary)] to-[#6b2fd6] p-6 text-white">
              <div className="text-xl font-bold">Premium'a Geç</div>
              <p className="mt-2 text-xs text-white/80">Sınırsız eşleşme, gelişmiş filtreler ve öncelikli görünürlük için üyelik planını seç.</p>

              <div className="mt-4 space-y-2">
                {MEMBERSHIP_PLANS.filter((plan) => plan.code !== "free").map((plan) => (
                  <div key={plan.code} className="rounded-2xl border border-white/25 bg-white/10 px-3 py-2">
                    <div className="flex items-center justify-between gap-2 text-sm font-semibold">
                      <span>{plan.name}</span>
                      <span>{typeof plan.pricesTry.monthly === "number" ? `${plan.pricesTry.monthly} ₺/ay` : "-"}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-white/80">
                      {typeof plan.pricesTry.annual === "number" ? `Yıllık ${plan.pricesTry.annual} ₺` : ""}
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="/panel/magaza"
                className="mt-4 block w-full rounded-2xl bg-white py-3 text-center text-sm font-bold text-[var(--panel-primary)]"
              >
                Planları İncele
              </a>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
